import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { AnimatePresence, m } from 'framer-motion';
import {
    Box,
    Button,
    CircularProgress,
    ClickAwayListener,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    InputAdornment,
    ListSubheader,
    Menu,
    MenuItem,
    Switch,
    Paper,
    Popper,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { HOST_API } from 'src/config';
import Iconify from '../Iconify';
import AssignedToLead from '../AssignedToLead';
import usePracticeStaff from '../../hooks/usePracticeStaff';
import BulkCommunicationPopup from '../dialogPopUp/BulkCommunicationPopup';
import { dispatch, useSelector } from '../../redux/store';
import { bulkArchivedOrRestore } from '../../redux/slices/lead';
import { updateError } from '../../redux/slices/user';
import { getAllTagItem } from '../../redux/slices/tagsCreate';
import { getPracticeTreatmentList } from '../../redux/slices/practiceTreatment';
import FilterTreatmentList from '../../utils/filtertreatmentList';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import useCustomSnackbar from '../../hooks/useCustomSnackbar';
import { CHECKLIST_ITEM_TYPE, DEPRECATED_CHECKLIST_ITEM_TYPES, TASKS_V2_CHANGED_EVENT } from '../../constants/tasksV2';
import { londonDateStr, londonDateStrFromApi } from '../../utils/taskDateUtils';
import {
    canonicalLeadIdFromFlatRowId,
    getSelectedLeadIdsForApi,
    getSelectedTaskTargetsForApi,
    pruneSelectedLeadIds,
} from '../../utils/selectionModel';

// ── Popover geometry ──────────────────────────────────────────────────────────
// The bar is a floating pill, so a panel anchored to one of its buttons sits flush
// against it and — for buttons near the right edge — runs off-screen. These
// modifiers lift the panel clear of the bar and keep it inside the viewport.
const BAR_POPPER_MODIFIERS = [
    { name: 'offset', options: { offset: [0, 12] } },
    { name: 'preventOverflow', options: { padding: 16 } },
    { name: 'flip', options: { padding: 16 } },
];

const barPopoverPaperSx = {
    p: 2,
    borderRadius: 2.5,
    border: '1px solid rgba(145, 158, 171, 0.16)',
    boxShadow: '0 16px 48px rgba(20, 22, 28, 0.22), 0 2px 8px rgba(20, 22, 28, 0.10)',
};

// The app sets a global `button { text-transform: capitalize !important }`, which
// turns "Apply to 3 leads" into "Apply To 3 Leads". A class-level !important beats
// the element selector, so bar buttons opt out explicitly.
const NO_CAPS = { textTransform: 'none !important' };

// ── "NHS treatment" recognition ───────────────────────────────────────────────
// There is NO NHS flag on PracticeTreatment/TreatmentItem — `healthcare_provider`
// lives on the LEAD, not on the treatment — so NHS-ness of a treatment can only be
// read off how the practice has named it. In practice that is a treatment category
// called "NHS" and/or treatment names carrying the word ("Children NHS App",
// "New NHS waiting list").
//
// This is deliberately only a FILTER default, never a constraint: it narrows a long
// list on the NHS board, and the toggle turns it off to reach every treatment. A
// false negative therefore costs one click, never a blocked action. If NHS treatments
// ever become first-class (an `is_nhs` boolean on PracticeTreatment), swap the
// predicate here and everything above it keeps working.
const NHS_WORD_RE = /\bNHS\b/i;

function isNhsTreatment(option) {
    return NHS_WORD_RE.test(option?.name || '') || NHS_WORD_RE.test(option?.category || '');
}

// ── Archive reasons ───────────────────────────────────────────────────────────
const ARCHIVE_REASONS = [
    { value: 'CREDIT_CHECK_FAILED', label: 'Credit check failed' },
    { value: 'TOO_EXPENSIVE', label: 'Too expensive' },
    { value: 'CHANGED_MIND', label: 'Changed mind' },
    { value: 'DUPLICATE_ENTRY', label: 'Duplicate entry' },
    { value: 'WANT_NHS', label: 'Want NHS' },
    { value: 'CLINICALLY_NOT_SUITABLE', label: 'Clinically not suitable' },
    { value: 'PRACTICE_TOO_FAR', label: 'Practice too far' },
    { value: 'ANOTHER_PRACTICE', label: 'Another practice' },
    { value: 'NO_RESPONSE', label: 'No response' },
    { value: 'OTHER', label: 'Other' },
];

// ── Checklist bulk helpers ────────────────────────────────────────────────────

// Per-item bulk-completion state.
// SOURCE OF TRUTH: CHECKLIST_ITEM_TYPE enum (src/constants/tasksV2.js) +
// TaskChecklistField.jsx per-type switch + the backend /complete/ action
// (apps/tasks/comment_views.py::ChecklistItemViewSet.complete).
//
// Product decision (869e48kpn R3): bulk Complete records YES only for the two
// explicit yes/no item types. Every other real checklist type is terminalized as
// SKIPPED with bulk_complete_origin provenance. This avoids manufacturing text,
// dates, sends, appointments, documents, payments, or lead-state decisions while
// still allowing the lead's checklist flow to finish server-side.
const BULK_YES_NO_TYPES = new Set([
    CHECKLIST_ITEM_TYPE.CONFIRMATION,
    CHECKLIST_ITEM_TYPE.YES_NO_TOGGLE,
]);

function isBulkYesNoType(itemType) {
    return BULK_YES_NO_TYPES.has(itemType);
}

// PAYMENT_METHOD is the one evidence-backed exception to the rule above (2026-08).
// It is a value step, not a yes/no — but when a payment IS already recorded against
// that row, terminalising it as SKIPPED is simply wrong: staff pressed Complete on a
// row where the money is logged and got a skipped step back (and, before the cell was
// made state-aware, no visible change at all). So: YES when the row already has a
// payment, SKIPPED when it does not. Nothing is invented either way — R3's intent
// (never manufacture a payment) is preserved.
//
// `paidRowIds` is a Set of row identities (String) supplied by the table, built from
// the same deposit_payment_data the payment column renders — no extra fetch. Rows on
// the flat tables are "<leadId>-<leadTreatmentId>", which is the right granularity:
// a payment belongs to one treatment, not to the whole lead.
function isBulkYesForItem(itemType, rowId, paidRowIds) {
    if (isBulkYesNoType(itemType)) return true;
    if (itemType === CHECKLIST_ITEM_TYPE.PAYMENT_METHOD) {
        return typeof paidRowIds?.has === 'function' && paidRowIds.has(String(rowId));
    }
    return false;
}

// Preserve yes/no semantics: both interactive yes/no variants record an explicit
// affirmative answer. A plain CONFIRMATION checkbox needs no response payload.
function buildCompleteBody(item) {
    if (
        item.item_type === CHECKLIST_ITEM_TYPE.YES_NO_TOGGLE
        || (
            item.item_type === CHECKLIST_ITEM_TYPE.CONFIRMATION
            && item.action_config?.requires_confirmation
        )
    ) {
        return { completion_state: 'YES', response_payload: { answer: 'yes' } };
    }
    return { completion_state: 'YES' };
}

// Informational grouping for the post-action summary. These items are POSTed as
// SKIPPED (not left pending); the groups explain what kind of action was skipped.
const SKIP_GROUP = {
    needs_send: new Set([
        CHECKLIST_ITEM_TYPE.EMAIL_SENT,
        CHECKLIST_ITEM_TYPE.WHATSAPP_SENT,
        CHECKLIST_ITEM_TYPE.SMS_SENT,
        CHECKLIST_ITEM_TYPE.SEND_CONSENT_FORM,
        CHECKLIST_ITEM_TYPE.SEND_REVIEW_REQUEST,
        CHECKLIST_ITEM_TYPE.SEND_PAYMENT_LINK,
        CHECKLIST_ITEM_TYPE.SEND_TREATMENT_PLAN,
        CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL,
    ]),
    needs_data: new Set([
        CHECKLIST_ITEM_TYPE.FREE_TEXT,
        CHECKLIST_ITEM_TYPE.MULTIPLE_CHOICE,
        CHECKLIST_ITEM_TYPE.ADD_NOTE,
        CHECKLIST_ITEM_TYPE.CURRENCY_AMOUNT,
        CHECKLIST_ITEM_TYPE.PAYMENT_METHOD,
        CHECKLIST_ITEM_TYPE.STAFF_PICKER,
        CHECKLIST_ITEM_TYPE.DATE_FIELD,
        CHECKLIST_ITEM_TYPE.SIMPLE_DATE,
    ]),
    needs_next_action: new Set([
        ...DEPRECATED_CHECKLIST_ITEM_TYPES,
        CHECKLIST_ITEM_TYPE.LEAD_NEXT_ACTION,
    ]),
    needs_artifact: new Set([
        CHECKLIST_ITEM_TYPE.UPLOAD_DOCUMENT,
        CHECKLIST_ITEM_TYPE.LINKED_CHECKLIST,
        CHECKLIST_ITEM_TYPE.SCHEDULE_REMINDER,
        CHECKLIST_ITEM_TYPE.BOOK_APPOINTMENT,
    ]),
};

function skipGroupFor(itemType) {
    const entry = Object.entries(SKIP_GROUP).find(([, s]) => s.has(itemType));
    return entry ? entry[0] : 'needs_data';
}

// callApi never throws — it returns { ok, status, data, error }.
// Helper to surface a readable message from a failed response.
function apiErrMsg(res) {
    const body = res?.data;
    if (typeof body === 'string') return body;
    // #111: DRF rejects (e.g. serializer.ValidationError(['msg'])) serialise as a
    // BARE ARRAY of strings — `["Task is already complete."]`. Read the first
    // string before the {detail}/{message} object-shape fallbacks, otherwise the
    // rep sees "HTTP 400" instead of the real reason.
    if (Array.isArray(body) && typeof body[0] === 'string') return body[0];
    if (body?.detail) return body.detail;
    if (body?.message) return body.message;
    return `HTTP ${res?.status || 'error'}`;
}

const TERMINAL_CHECKLIST_STATES = new Set(['YES', 'SKIPPED']);

function requireTerminalChecklistOutcome(res) {
    if (!res.ok) throw new Error(apiErrMsg(res));
    const returnedState = res.data?.completion_state;
    if (!TERMINAL_CHECKLIST_STATES.has(returnedState)) {
        throw new Error(returnedState == null
            ? 'Task item response did not confirm a terminal state'
            : `Task item remained ${returnedState}`);
    }
    return res;
}

function requireTerminalTaskOutcome(res) {
    if (!res.ok) throw new Error(apiErrMsg(res));
    const returnedStatus = res.data?.status;
    if (!['completed', 'archived'].includes(returnedStatus)) {
        throw new Error(returnedStatus == null
            ? 'Task response did not confirm a terminal status'
            : `Task remained ${returnedStatus}`);
    }
    return res;
}

function requireTerminalReminderOutcome(res) {
    if (!res.ok) throw new Error(apiErrMsg(res));
    if (res.data?.is_completed !== true) {
        throw new Error('Reminder response did not confirm completion');
    }
    return res;
}

function emitTasksV2Changed() {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(TASKS_V2_CHANGED_EVENT));
}

// ── Bucket-window reminder helpers ────────────────────────────────────────────
//
// A "standalone reminder" is a V1 Reminder row (integer id) stored in
// /api/v1/practices/{id}/reminders/.  The new-lead-table task buckets
// (due_today / overdue / upcoming) are date-driven:
//   overdue   → reminder.datetime date <  today (strictly past)
//   due_today → reminder.datetime date <= today (overdue ∪ today)
//   upcoming  → reminder.datetime date >  today
// This mirrors apps/tasks/new_lead_task_buckets.py bucket logic on the BE.
//
// Matching is done client-side after a single list fetch per lead so we avoid
// adding new BE query params and keep the implementation contained to the FE.

/**
 * Core bucket-window comparison, shared by reminders (A′) and standalone tasks
 * (Option 2).  Both callers derive a Europe/London calendar date string
 * (YYYY-MM-DD) via `londonDateStrFromApi` and compare it against today.
 *
 * ## Bucket semantics (mirror `apps/tasks/new_lead_task_buckets.py`)
 * `due_today` at the BE means "due on or before today" (overdue ∪ today) —
 * a rep on the Due-Today board sees ALL leads that need attention today,
 * including overdue ones.  `overdue` is a narrower sub-bucket (strict past).
 *   overdue   → dateStr  <  today (strictly past)
 *   due_today → dateStr <= today (overdue ∪ today)
 *   upcoming  → dateStr  >  today
 *
 * @param {string|null} dateStr  YYYY-MM-DD London calendar date (or falsy).
 * @param {'overdue'|'due_today'|'upcoming'|string|undefined} bucket
 * @param {string} todayStr  YYYY-MM-DD today in Europe/London.
 * @returns {boolean}
 */
function dateStrMatchesBucket(dateStr, bucket, todayStr) {
    if (!bucket || !dateStr) return false;
    if (bucket === 'overdue')   return dateStr < todayStr;
    if (bucket === 'due_today') return dateStr <= todayStr;   // overdue ∪ today
    if (bucket === 'upcoming')  return dateStr > todayStr;
    return false;
}

/**
 * Returns true if the reminder's due date falls in the given bucket window.
 *
 * Both sides are compared as **Europe/London** calendar dates (YYYY-MM-DD
 * strings) to match the backend bucket logic exactly.
 *
 * ## Date format
 * The DRF `DATETIME_FORMAT` is `"%d-%m-%Y %H:%M:%S.%fZ"` (settings.py), so
 * the wire value is e.g. `"30-06-2026 09:00:00.000000Z"` — DD-MM-YYYY, not
 * ISO.  `londonDateStrFromApi` reformats it to YYYY-MM-DD without any TZ
 * conversion (the date portion is already London-local — Django renders in
 * Europe/London then appends a literal `Z`; see `taskDateUtils.js` docstring
 * and `smartReminderRules.js:9`).
 *
 * Delegates the bucket compare to `dateStrMatchesBucket` (shared with tasks).
 *
 * @param {{ datetime: string, is_completed: boolean }} reminder
 * @param {'overdue'|'due_today'|'upcoming'|string|undefined} bucket
 * @param {Date} now  - The reference "today" (injected so tests can fix it).
 */
function reminderMatchesBucket(reminder, bucket, now) {
    if (!bucket || !reminder?.datetime) return false;

    // Parse the reminder's London calendar date from the DRF wire format
    // (DD-MM-YYYY HH:mm:ss.ffffffZ) or ISO 8601 (test fixtures).
    const reminderDateStr = londonDateStrFromApi(reminder.datetime);
    if (!reminderDateStr) return false;

    // Compute "today" in Europe/London (DST-safe via Intl) to match the BE bucket.
    const todayStr = londonDateStr(0, now);
    return dateStrMatchesBucket(reminderDateStr, bucket, todayStr);
}

// ── Bucket-window standalone-task helpers (Option 2) ──────────────────────────
//
// A "checklist-less standalone task" is a V2 Task with `checklist_items: []`
// and no V1 Reminder mirror — e.g. an auto-follow-up task
// (`is_auto_follow_up_reminder: true`).  Neither the checklist path (nothing to
// tick) nor A′ (no reminder row) completes it, so the rep can't clear it from
// the new-lead-table.  Option 2 completes each selected lead's OPEN,
// checklist-less tasks whose `due_at` is in the current bucket window via
// `POST /tasks/{id}/complete/ {force:false}` — folded into the same union
// lead-count + toast that A′ produces.

// Task-level open statuses (Task.status is lowercase — TaskStatus enum in
// apps/tasks/choices.py).  COMPLETED / ARCHIVED are terminal.
const OPEN_TASK_STATUSES = new Set(['pending', 'in_progress']);

/**
 * Returns true if the task's `due_at` falls in the given bucket window.
 *
 * ⚠️ `task.due_at` is DRF wire-format `DD-MM-YYYY HH:mm:ss.ffffffZ` (day-first,
 * NOT ISO — see `taskDateUtils.js`).  MUST parse via `londonDateStrFromApi`;
 * a `.slice(0,10)` or `new Date(raw)` would mis-bucket every task.  Missing /
 * unparseable `due_at` → false (fail safe, never throws).
 *
 * @param {{ due_at?: string }} task
 * @param {'overdue'|'due_today'|'upcoming'|string|undefined} bucket
 * @param {Date} now  - The reference "today" (injected so tests can fix it).
 * @returns {boolean}
 */
function taskMatchesBucket(task, bucket, now) {
    if (!bucket || !task?.due_at) return false;
    const dueStr = londonDateStrFromApi(task.due_at);
    if (!dueStr) return false;
    const todayStr = londonDateStr(0, now);
    return dateStrMatchesBucket(dueStr, bucket, todayStr);
}

/**
 * Filters the already-loaded per-lead task arrays (from `fetchTaskItems`) to the
 * Option-2-eligible checklist-less standalone tasks.
 *
 * Eligibility — ALL must hold (the over-completion guard):
 *   1. `Array.isArray(task.checklist_items) && task.checklist_items.length === 0`
 *      — genuinely checklist-less (missing/non-array array → ineligible, fail safe).
 *   2. Task status is OPEN (`pending` / `in_progress`) — not completed/archived.
 *   3. `taskMatchesBucket(task, bucket, now)` — due_at in the current window.
 *
 * A task with ANY checklist items stays on the checklist path so required items
 * are still enforced.  When bucket is undefined (contacts mode) nothing is
 * selected — Option 2 is inert there, same as A′.
 *
 * @param {Array<{ leadId: number|string, tasks: any[] }>} leadTasks
 * @param {string|undefined} bucket
 * @param {Date} now
 * @returns {{ perLead: Map<number|string, (number|string)[]>, taskIds: (number|string)[] }}
 */
function selectChecklistlessBucketTasks(leadTasks, bucket, now) {
    const perLead = new Map();
    const taskIds = [];
    if (!bucket || !leadTasks?.length) return { perLead, taskIds };

    leadTasks.forEach(({ leadId, tasks }) => {
        const eligible = (tasks || []).filter((task) =>
            Array.isArray(task?.checklist_items) &&
            task.checklist_items.length === 0 &&
            OPEN_TASK_STATUSES.has(task.status) &&
            taskMatchesBucket(task, bucket, now)
        );
        if (eligible.length > 0) {
            const ids = eligible.map((t) => t.id);
            perLead.set(leadId, ids);
            taskIds.push(...ids);
        }
    });

    return { perLead, taskIds };
}

/**
 * Completes each eligible checklist-less standalone task via
 * `POST /tasks/{id}/complete/ {force:false}`, reusing the already-loaded
 * `leadTasks` (no second /tasks/ fetch).
 *
 * `force:false` is a HARD invariant — it keeps the BE required-checklist gate
 * (services.py) as a second guard behind the FE eligibility filter.  NEVER
 * pass force:true from this path.
 *
 * Failure posture: `callApi` returns `{ok:false}` on non-2xx (it does NOT
 * throw); each complete runs inside `Promise.allSettled`, so a failed task is
 * a per-task failure that NEVER suppresses the checklist / reminder successes.
 *
 * @param {number|string} practiceId
 * @param {Array<{ leadId: number|string, tasks: any[] }>} leadTasks
 * @param {string|undefined} bucket
 * @param {Date} [now=new Date()]
 * @returns {Promise<{ total: number, failed: number, succeededLeadIds: Set<string>, failedLeadIds: Set<string> }>}
 */
async function completeChecklistlessBucketTasks(
    practiceId,
    leadTasks,
    bucket,
    now,
    isOperationCurrent
) {
    const { perLead } = selectChecklistlessBucketTasks(leadTasks, bucket, now ?? new Date());
    const callMeta = []; // [{ leadId }] — parallel index to calls
    const calls = [];
    perLead.forEach((taskIds, leadId) => {
        taskIds.forEach((taskId) => {
            assertOperationCurrent(isOperationCurrent);
            callMeta.push({ leadId });
            calls.push(
                callApi(
                    `/api/v1/practices/${practiceId}/tasks/${taskId}/complete/`,
                    'POST',
                    { force: false }
                ).then(requireTerminalTaskOutcome)
            );
        });
    });
    const results = await Promise.allSettled(calls);
    const succeededLeadIds = new Set(
        results
            .map((r, idx) => (r.status === 'fulfilled' ? String(callMeta[idx].leadId) : null))
            .filter(Boolean)
    );
    const failedLeadIds = new Set(
        results
            .map((r, idx) => (r.status === 'rejected' ? String(callMeta[idx].leadId) : null))
            .filter(Boolean)
    );
    return {
        total: calls.length,
        failed: results.filter((r) => r.status === 'rejected').length,
        succeededLeadIds,
        failedLeadIds,
    };
}

/**
 * Filters a list of reminders to those that match the bucket window AND are
 * not already completed.
 *
 * @param {Array<{ id: number|string, datetime: string, is_completed: boolean }>} reminders
 * @param {string|undefined} bucket
 * @param {Date} now
 * @returns {Array}
 */
function selectBucketReminders(reminders, bucket, now) {
    if (!bucket || !reminders?.length) return [];
    return reminders.filter(
        (r) => !r.is_completed && reminderMatchesBucket(r, bucket, now)
    );
}

const MAX_SCOPED_PAGINATION_PAGES = 1000;

class BulkOperationSupersededError extends Error {
    constructor() {
        super('Bulk operation superseded');
        this.name = 'BulkOperationSupersededError';
    }
}

function assertOperationCurrent(isOperationCurrent) {
    if (typeof isOperationCurrent === 'function' && !isOperationCurrent()) {
        throw new BulkOperationSupersededError();
    }
}

function isBulkOperationSuperseded(error) {
    return error instanceof BulkOperationSupersededError;
}

function paginationScopePath(practiceId, resource) {
    return `/api/v1/practices/${practiceId}/${resource}/`;
}

function explicitUrlPort(rawUrl) {
    const authority = rawUrl.match(/^[a-z][a-z\d+.-]*:\/\/([^/?#]*)/i)?.[1];
    if (authority == null) return null;
    const hostPort = authority.slice(authority.lastIndexOf('@') + 1);
    if (hostPort.startsWith('[')) {
        const bracket = hostPort.indexOf(']');
        return bracket >= 0 && hostPort[bracket + 1] === ':'
            ? hostPort.slice(bracket + 2)
            : '';
    }
    const colon = hostPort.lastIndexOf(':');
    return colon >= 0 ? hostPort.slice(colon + 1) : '';
}

function scopedPaginationUrl(rawUrl, {
    practiceId,
    resource,
    leadId,
    leadTreatmentId,
    requiredParams = {},
}) {
    if (typeof rawUrl !== 'string' || !rawUrl.trim()) {
        throw new Error(`Invalid ${resource} pagination URL`);
    }

    const raw = rawUrl.trim();
    const configuredBase = typeof HOST_API === 'string' ? HOST_API.trim() : '';
    const fallbackBase = 'https://crm-api.invalid';
    const isSchemeRelative = raw.startsWith('//');
    const hasExplicitScheme = /^[a-z][a-z\d+.-]*:/i.test(raw);
    const isAbsoluteHttp = /^https?:\/\//i.test(raw);
    if (isSchemeRelative || (hasExplicitScheme && !isAbsoluteHttp)) {
        throw new Error(`Untrusted ${resource} pagination URL`);
    }

    let apiBase;
    let candidate;
    try {
        apiBase = new URL(configuredBase || fallbackBase);
        candidate = new URL(raw, apiBase);
    } catch (_err) {
        throw new Error(`Invalid ${resource} pagination URL`);
    }

    if (
        !['http:', 'https:'].includes(apiBase.protocol)
        || !['http:', 'https:'].includes(candidate.protocol)
    ) {
        throw new Error(`Invalid ${resource} pagination URL`);
    }
    // DRF can serialize the proxy-facing HTTP scheme even when HOST_API is HTTPS.
    // Accept that one-way mismatch only after pinning hostname and the explicit
    // port form, then return a relative URL below so callApi keeps HOST_API HTTPS.
    const absoluteAuthorityMatches = isAbsoluteHttp
        && configuredBase
        && candidate.hostname === apiBase.hostname
        && explicitUrlPort(raw) === explicitUrlPort(configuredBase)
        && (
            candidate.protocol === apiBase.protocol
            || (apiBase.protocol === 'https:' && candidate.protocol === 'http:')
        );
    if (
        raw.includes('#')
        || candidate.username
        || candidate.password
        || (isAbsoluteHttp ? !absoluteAuthorityMatches : candidate.origin !== apiBase.origin)
    ) {
        throw new Error(`Untrusted ${resource} pagination URL`);
    }
    if (candidate.pathname !== paginationScopePath(practiceId, resource)) {
        throw new Error(`${resource} pagination escaped the selected practice`);
    }

    const expectedParams = {
        lead: String(leadId),
        ...Object.fromEntries(
            Object.entries(requiredParams).map(([key, value]) => [key, String(value)])
        ),
    };
    Object.entries(expectedParams).forEach(([key, expected]) => {
        const values = candidate.searchParams.getAll(key);
        if (values.length !== 1 || values[0] !== expected) {
            throw new Error(`${resource} pagination changed the selected lead scope`);
        }
    });

    const allowedParams = new Set([
        ...Object.keys(expectedParams),
        'lead_treatment',
        'offset',
        'page',
        'cursor',
    ]);
    if ([...candidate.searchParams.keys()].some((key) => !allowedParams.has(key))) {
        throw new Error(`${resource} pagination added an unexpected filter`);
    }

    const treatmentValues = candidate.searchParams.getAll('lead_treatment');
    if (
        leadTreatmentId == null
            ? treatmentValues.length !== 0
            : treatmentValues.length !== 1 || treatmentValues[0] !== String(leadTreatmentId)
    ) {
        throw new Error(`${resource} pagination changed the selected treatment scope`);
    }

    return `${candidate.pathname}${candidate.search}`;
}

async function fetchScopedDrfSnapshot(initialUrl, scope, errorLabel, isOperationCurrent) {
    let pageUrl = scopedPaginationUrl(initialUrl, scope);
    const visited = new Set();
    const rows = [];
    let expectedCount = null;

    for (let page = 0; page < MAX_SCOPED_PAGINATION_PAGES; page += 1) {
        assertOperationCurrent(isOperationCurrent);
        if (visited.has(pageUrl)) {
            throw new Error(`Cyclic ${scope.resource} pagination for ${errorLabel}`);
        }
        visited.add(pageUrl);

        // Pagination is intentionally sequential: each scoped URL comes from the prior page.
        // eslint-disable-next-line no-await-in-loop
        const res = await callApi(pageUrl, 'GET');
        assertOperationCurrent(isOperationCurrent);
        if (!res.ok) {
            throw new Error(`Failed to fetch ${scope.resource} for ${errorLabel}: ${apiErrMsg(res)}`);
        }
        if (!res.data || typeof res.data !== 'object' || !Array.isArray(res.data.results)) {
            throw new Error(`Invalid ${scope.resource} response for ${errorLabel}`);
        }

        if (res.data.count != null) {
            const pageCount = Number(res.data.count);
            if (!Number.isSafeInteger(pageCount) || pageCount < 0) {
                throw new Error(`Invalid ${scope.resource} count for ${errorLabel}`);
            }
            if (expectedCount == null) {
                expectedCount = pageCount;
            } else if (pageCount !== expectedCount) {
                throw new Error(`${scope.resource} changed during pagination for ${errorLabel}`);
            }
        }
        rows.push(...res.data.results);

        if (res.data.next == null) {
            if (
                expectedCount != null
                && dedupeRowsByStableId(rows, scope.resource, errorLabel).length !== expectedCount
            ) {
                throw new Error(`${scope.resource} changed during pagination for ${errorLabel}`);
            }
            return { rows, pageCount: visited.size };
        }
        pageUrl = scopedPaginationUrl(res.data.next, scope);
    }

    throw new Error(`Too many ${scope.resource} pages for ${errorLabel}`);
}

async function fetchAllScopedDrfPages(initialUrl, scope, errorLabel, isOperationCurrent) {
    const first = await fetchScopedDrfSnapshot(
        initialUrl,
        scope,
        errorLabel,
        isOperationCurrent
    );
    if (first.pageCount === 1) return first.rows;

    // Offset pagination has no snapshot token. Verify every multi-page result
    // once more before callers detail-fetch or mutate anything; equal counts
    // alone cannot detect rows shifting between offsets during discovery.
    const second = await fetchScopedDrfSnapshot(
        initialUrl,
        scope,
        errorLabel,
        isOperationCurrent
    );
    const stableIds = ({ rows }) => dedupeRowsByStableId(
        rows,
        scope.resource,
        errorLabel
    ).map(({ id }) => String(id)).sort();
    const firstIds = stableIds(first);
    const secondIds = stableIds(second);
    if (
        firstIds.length !== secondIds.length
        || firstIds.some((id, index) => id !== secondIds[index])
    ) {
        throw new Error(`${scope.resource} changed between pagination reads for ${errorLabel}`);
    }
    return second.rows;
}

function dedupeRowsByStableId(rows, resource, errorLabel) {
    const seen = new Set();
    return rows.reduce((deduped, row) => {
        const id = row?.id;
        const stableId = id == null ? '' : String(id);
        if (!/^[A-Za-z\d_-]+$/.test(stableId)) {
            throw new Error(`Invalid ${resource} identity for ${errorLabel}`);
        }
        if (!seen.has(stableId)) {
            seen.add(stableId);
            deduped.push(row);
        }
        return deduped;
    }, []);
}

/**
 * Fetches open reminders for each selected lead and returns only those that
 * fall in the current bucket window.
 *
 * When bucket is undefined/null (contacts mode), returns per-lead empty arrays
 * without making any API calls — non-task-mode bulk-complete must not touch reminders.
 *
 * @param {number|string} practiceId
 * @param {Array<number|string|object>} targetsOrLeadIds
 * @param {string|undefined} bucket   - 'due_today' | 'overdue' | 'upcoming' | undefined
 * @param {Date} [now=new Date()]
 * @returns {Promise<Array<{ leadId: number|string, reminders: Array }>>}
 */
async function fetchBucketReminders(
    practiceId,
    targetsOrLeadIds,
    bucket,
    now,
    isOperationCurrent
) {
    const targets = normalizeTaskTargets(targetsOrLeadIds);
    const effectiveNow = now ?? new Date();
    assertOperationCurrent(isOperationCurrent);
    // Guard: no bucket → contacts mode, skip entirely.
    if (!bucket) {
        return targets.map(({ rowId }) => ({ leadId: rowId, reminders: [] }));
    }

    const perLead = await Promise.all(
        targets.map(async (target) => {
            const treatmentFilter = target.leadTreatmentId == null
                ? ''
                : `&lead_treatment=${target.leadTreatmentId}`;
            // Tasks V2 keeps a V1 Reminder shadow for compatibility. That shadow
            // represents the same work as its mapped Task, so including it here
            // would mutate and count one logical item through both APIs.
            const all = dedupeRowsByStableId(
                await fetchAllScopedDrfPages(
                    `/api/v1/practices/${practiceId}/reminders/?lead=${target.leadId}${treatmentFilter}&exclude_migrated=true&is_completed=false&ordering=datetime&limit=100`,
                    {
                        practiceId,
                        resource: 'reminders',
                        leadId: target.leadId,
                        leadTreatmentId: target.leadTreatmentId,
                        requiredParams: {
                            exclude_migrated: 'true',
                            is_completed: 'false',
                            ordering: 'datetime',
                            limit: '100',
                        },
                    },
                    `lead ${target.leadId}`,
                    isOperationCurrent
                ),
                'reminder',
                `lead ${target.leadId}`
            );
            if (target.leadTreatmentId != null) {
                const hasUnscopedReminder = all.some((reminder) => {
                    const reminderTreatment = reminder?.lead_treatment;
                    const reminderTreatmentId = reminderTreatment && typeof reminderTreatment === 'object'
                        ? reminderTreatment.id
                        : reminderTreatment;
                    return String(reminderTreatmentId ?? '') !== String(target.leadTreatmentId);
                });
                if (hasUnscopedReminder) {
                    throw new Error(`Reminder scope could not be verified for row ${target.rowId}`);
                }
            }
            return {
                leadId: target.rowId,
                reminders: selectBucketReminders(all, bucket, effectiveNow),
            };
        })
    );
    return perLead;
}

/**
 * Union of every lead that had ≥1 successful completion across the three
 * bulk-complete channels: checklist items, bucket-window reminders (A′), and
 * checklist-less standalone tasks (Option 2).  Returned as an array of STRING
 * lead ids so the parent's String-keyed prune matches regardless of whether the
 * source ids are numeric (checklist / task) or already strings (reminder).
 *
 * This is the row-exit / prune set — previously only the checklist-only ids
 * (`completedLeadIds`) were used, so a lead completed purely via a reminder or a
 * standalone task lingered in the table and stayed selected.
 *
 * @param {{ checklistLeadIds?: Iterable, reminderLeadIds?: Iterable, taskLeadIds?: Iterable }} sets
 * @returns {string[]}
 */
function buildUnionLeadIds({ checklistLeadIds = [], reminderLeadIds = [], taskLeadIds = [] }) {
    return [...new Set([
        ...[...checklistLeadIds].map((id) => String(id)),
        ...[...reminderLeadIds].map((id) => String(id)),
        ...[...taskLeadIds].map((id) => String(id)),
    ])];
}

function countCanonicalLeads(rowIds = []) {
    return new Set(rowIds.map((rowId) => (
        canonicalLeadIdFromFlatRowId(rowId) ?? String(rowId)
    ))).size;
}

/**
 * Builds the truthful completion toast message, merging checklist-task counts,
 * standalone-task counts (Option 2), and reminder counts.  Any part may be zero.
 *
 * `taskTotal` is the checklist-item completion count; `standaloneTaskTotal` is
 * the checklist-less V2 task completion count (Option 2).  Both are reported to
 * the rep as "tasks" (they are indistinguishable in the UI), so their succeeded
 * counts are summed into a single "N tasks" phrase. `standaloneTaskFailed`
 * drives the retry suffix for partial mutation failures.
 *
 * @param {{
 *   taskTotal: number,
 *   taskFailed: number,
 *   standaloneTaskTotal?: number,
 *   standaloneTaskFailed?: number,
 *   reminderTotal: number,
 *   reminderFailed: number,
 *   leadsWithItems: number,
 *   taskSkipNote?: string,
 * }} opts
 * @returns {string}
 */
function buildBulkCompleteToast({
    taskTotal,
    taskFailed,
    standaloneTaskTotal = 0,
    standaloneTaskFailed = 0,
    reminderTotal,
    reminderFailed,
    leadsWithItems,
    taskSkipNote = '',
}) {
    const taskSucceeded = (taskTotal - taskFailed) + (standaloneTaskTotal - standaloneTaskFailed);
    const reminderSucceeded = reminderTotal - reminderFailed;

    const pl = (n, word) => `${n} ${word}${n !== 1 ? 's' : ''}`;

    const parts = [];
    if (taskSucceeded > 0)     parts.push(pl(taskSucceeded,     'task'));
    if (reminderSucceeded > 0) parts.push(pl(reminderSucceeded, 'reminder'));

    const leadsLabel = pl(leadsWithItems, 'lead');

    // Non-fatal warning when some standalone-task completes failed. The
    // checklist / reminder successes still stand; only these tasks need a retry.
    const standaloneFailNote = standaloneTaskFailed > 0
        ? ` (${standaloneTaskFailed} task${standaloneTaskFailed === 1 ? '' : 's'} couldn't be completed — retry to clear those.)`
        : '';

    if (parts.length === 0) {
        // Nothing succeeded — caller handles the info/warning snackbar variant.
        return `No items completed.${taskSkipNote}${standaloneFailNote}`;
    }

    return `Completed ${parts.join(' and ')} across ${leadsLabel}.${taskSkipNote}${standaloneFailNote}`;
}

function normalizeTaskTargets(targetsOrLeadIds = []) {
    const seen = new Set();
    return targetsOrLeadIds.reduce((targets, value) => {
        const source = value && typeof value === 'object'
            ? value
            : { rowId: value, leadId: value, leadTreatmentId: null };
        const leadId = Number(source.leadId);
        const leadTreatmentId = source.leadTreatmentId == null
            ? null
            : Number(source.leadTreatmentId);
        const rowId = source.rowId ?? source.leadId;
        const valid = Number.isSafeInteger(leadId)
            && leadId > 0
            && (
                leadTreatmentId == null
                || (Number.isSafeInteger(leadTreatmentId) && leadTreatmentId > 0)
            );
        const identity = String(rowId ?? '');
        if (!valid || !identity || seen.has(identity)) return targets;
        seen.add(identity);
        targets.push({ rowId, leadId, leadTreatmentId });
        return targets;
    }, []);
}

function taskListUrl(practiceId, { leadId, leadTreatmentId }) {
    const treatmentFilter = leadTreatmentId == null
        ? ''
        : `&lead_treatment=${leadTreatmentId}`;
    return `/api/v1/practices/${practiceId}/tasks/?lead=${leadId}${treatmentFilter}&limit=50`;
}

async function fetchTaskItems(practiceId, targetsOrLeadIds, isOperationCurrent) {
    const targets = normalizeTaskTargets(targetsOrLeadIds);
    // task-template-instances and /tasks/ list both omit checklist_items.
    // Use /tasks/?lead= to enumerate tasks, then detail-fetch each for checklist_items.
    const perLead = await Promise.all(
        targets.map(async (target) => {
            const taskStubs = dedupeRowsByStableId(
                await fetchAllScopedDrfPages(
                    taskListUrl(practiceId, target),
                    {
                        practiceId,
                        resource: 'tasks',
                        leadId: target.leadId,
                        leadTreatmentId: target.leadTreatmentId,
                        requiredParams: { limit: '50' },
                    },
                    `lead ${target.leadId}`,
                    isOperationCurrent
                ),
                'task',
                `lead ${target.leadId}`
            );
            const fetchedTasks = await Promise.all(
                taskStubs.map(async (stub) => {
                    assertOperationCurrent(isOperationCurrent);
                    const detail = await callApi(
                        `/api/v1/practices/${practiceId}/tasks/${stub.id}/`,
                        'GET'
                    );
                    assertOperationCurrent(isOperationCurrent);
                    if (!detail.ok) throw new Error(`Failed to fetch task ${stub.id}: ${apiErrMsg(detail)}`);
                    if (
                        !detail.data
                        || typeof detail.data !== 'object'
                        || String(detail.data.id ?? '') !== String(stub.id)
                    ) {
                        throw new Error(`Task identity could not be verified for ${stub.id}`);
                    }
                    return detail.data;
                })
            );
            const tasks = target.leadTreatmentId == null
                ? fetchedTasks
                : fetchedTasks.filter((task) => {
                    const taskTreatment = task?.lead_treatment;
                    const taskTreatmentId = taskTreatment && typeof taskTreatment === 'object'
                        ? taskTreatment.id
                        : taskTreatment;
                    // The server query is treatment-filtered. Keep a second,
                    // fail-closed check at the mutation boundary so an older or
                    // regressed list endpoint cannot widen a flat-row action.
                    return String(taskTreatmentId ?? '') === String(target.leadTreatmentId);
                });
            // Keep the historical `leadId` property name for helper/test
            // compatibility, but its value is the exact DataGrid row identity.
            // V1 rows remain numeric lead ids; flat rows are lead-treatment ids.
            return { leadId: target.rowId, tasks };
        })
    );
    return perLead;
}

async function prepareBulkCompleteDiscovery(
    practiceId,
    targetsOrLeadIds,
    bucket,
    isOperationCurrent
) {
    const [leadTasks, bucketReminders] = await Promise.all([
        fetchTaskItems(practiceId, targetsOrLeadIds, isOperationCurrent),
        fetchBucketReminders(
            practiceId,
            targetsOrLeadIds,
            bucket,
            undefined,
            isOperationCurrent
        ),
    ]);
    assertOperationCurrent(isOperationCurrent);
    return { leadTasks, bucketReminders };
}

async function bulkChecklistAction(
    practiceId,
    targetsOrLeadIds,
    endpoint,
    body,
    isOperationCurrent,
    discoveredLeadTasks,
    paidRowIds
) {
    const leadTasks = discoveredLeadTasks
        ?? await fetchTaskItems(practiceId, targetsOrLeadIds, isOperationCurrent);
    assertOperationCurrent(isOperationCurrent);
    // Per-target counts so callers can produce truthful toasts. Targets are
    // lead rows on Booked and exact treatment rows on the two flat tables:
    //   - leadsWithItems: how many selected leads contributed at least one
    //     actionable checklist item (powers "N tasks completed across M leads")
    //   - total === 0 means no selected lead had any actionable items, which
    //     must surface as an info toast, NOT a success toast.
    //
    // When isBulkYesComplete, yes/no items are completed YES and every other
    // checklist type is posted SKIPPED with bulk_complete_origin provenance.
    // Manual Skip/Pause paths remain unchanged.
    const isBulkYesComplete = endpoint === 'complete' && body?.completion_state === 'YES';
    let leadsWithItems = 0;
    // Per-lead metadata collected while building the flat calls array so we can gate
    // completedLeadIds AFTER Promise.allSettled.
    const perLeadMeta = []; // [{ leadId: row identity, callCount }]
    // Parallel to calls: one entry per call, used post-settle to build the named
    // failure list for P1 UX (names failed items instead of showing a bare count).
    const itemMeta = []; // [{ leadId, itemTitle, autoSkipGroup }]
    const calls = [];
    leadTasks.forEach(({ leadId, tasks }) => {
        let callCount = 0;
        tasks.forEach((task) => {
            (task.checklist_items || []).forEach((item) => {
                if (!['PENDING', 'PAUSED', 'IN_PROGRESS'].includes(item.completion_state)) return;
                if (isBulkYesComplete && !isBulkYesForItem(item.item_type, leadId, paidRowIds)) {
                    const group = skipGroupFor(item.item_type);
                    assertOperationCurrent(isOperationCurrent);
                    calls.push(
                        callApi(
                            `/api/v1/practices/${practiceId}/tasks/${task.id}/checklist/${item.id}/complete/`,
                            'POST',
                            { completion_state: 'SKIPPED', response_payload: { bulk_complete_origin: true } }
                        ).then(requireTerminalChecklistOutcome)
                    );
                    itemMeta.push({
                        leadId,
                        itemTitle: item.title || 'item',
                        autoSkipGroup: group,
                    });
                    callCount += 1;
                    return;
                }
                // For YES-complete, build a per-item body (avoids the bogus
                // {answer:'manual'} and supplies the required default for
                // confirmation-with-a-question). Skip/other paths use the
                // shared body unchanged.
                const itemBody = isBulkYesComplete ? buildCompleteBody(item) : body;
                assertOperationCurrent(isOperationCurrent);
                calls.push(
                    callApi(
                        `/api/v1/practices/${practiceId}/tasks/${task.id}/checklist/${item.id}/${endpoint}/`,
                        'POST',
                        itemBody
                    ).then((res) => {
                        if (endpoint === 'complete') return requireTerminalChecklistOutcome(res);
                        if (!res.ok) throw new Error(apiErrMsg(res));
                        return res;
                    })
                );
                itemMeta.push({
                    leadId,
                    itemTitle: item.title || 'item',
                    autoSkipGroup: null,
                });
                callCount += 1;
            });
        });
        if (callCount > 0) leadsWithItems += 1;
        perLeadMeta.push({ leadId, callCount });
    });
    const results = await Promise.allSettled(calls);
    const failures = results.filter((r) => r.status === 'rejected');
    // P1: named failure list — pairs each rejected result with its item metadata so
    // the caller can surface "Couldn't complete 'Title'" instead of a bare error count.
    // itemMeta and calls are built in lock-step so indices always align.
    const failedItems = results
        .map((r, idx) => (r.status === 'rejected' ? itemMeta[idx] : null))
        .filter(Boolean);
    // Auto-skip reporting is derived only after settlement. A rejected SKIPPED
    // request is still pending on the server and must never appear in the skip
    // dialog or inflate the skipped count shown to the user.
    const autoSkipAttempts = itemMeta.filter(({ autoSkipGroup }) => autoSkipGroup);
    const settledState = (result) => (
        result.status === 'fulfilled'
            ? result.value?.data?.completion_state
            : null
    );
    const successfulAutoSkips = results
        .map((result, index) => (
            settledState(result, index) === 'SKIPPED' && itemMeta[index]?.autoSkipGroup
                ? itemMeta[index]
                : null
        ))
        .filter(Boolean);
    const failedAutoSkipCount = results
        .filter((result, index) => result.status === 'rejected' && itemMeta[index]?.autoSkipGroup)
        .length;
    const skippedLeadsByGroup = {
        needs_send:        new Set(),
        needs_data:        new Set(),
        needs_next_action: new Set(),
        needs_artifact:    new Set(),
    };
    successfulAutoSkips.forEach(({ leadId, autoSkipGroup }) => {
        skippedLeadsByGroup[autoSkipGroup].add(String(leadId));
    });
    const totalAutoSkipped = successfulAutoSkips.length;
    // Trust the backend's per-item terminal state. A racing genuine staff
    // decision may make a synthetic SKIPPED request return the already-stored
    // YES row; that is a successful completion, not an auto-skip for reporting.
    const totalYesCompleted = results.filter(
        (result, index) => settledState(result, index) === 'YES'
    ).length;
    const failedYesCount = results.filter(
        (result, index) => result.status === 'rejected' && !itemMeta[index]?.autoSkipGroup
    ).length;
    // Build completedLeadIds post-settle: slice each lead's result window and confirm
    // all calls succeeded. Auto-SKIPPED items are terminal server-side.
    const completedLeadIds = [];
    // P0: skip groups surfaced as a persistent dialog by NewLead.jsx (handleItemsSkipped).
    const skippedGroups = {
        needs_send:        [...skippedLeadsByGroup.needs_send],
        needs_data:        [...skippedLeadsByGroup.needs_data],
        needs_next_action: [...skippedLeadsByGroup.needs_next_action],
        needs_artifact:    [...skippedLeadsByGroup.needs_artifact],
    };
    // Build completedLeadIds (for row-removal animation) and checklistSucceededLeadIds
    // (for toast union count) in one pass.
    //
    // completedLeadIds: leads where ALL checklist calls succeeded.
    //
    // checklistSucceededLeadIds: leads where AT LEAST ONE checklist call succeeded.
    //   Used by the caller to union with reminderLeadIds for the "across M leads" toast
    //   count (I1 fix).  A lead that had some failed calls but also some succeeded still
    //   shows in the count — the partial-failure toast path already flags the failures.
    const checklistSucceededLeadIds = new Set();
    let callIdx = 0;
    perLeadMeta.forEach(({ leadId, callCount }) => {
        if (callCount > 0) {
            const leadResults = results.slice(callIdx, callIdx + callCount);
            const leadFailed  = leadResults.some((r) => r.status === 'rejected');
            const leadAnyOk   = leadResults.some((r) => r.status === 'fulfilled');
            if (!leadFailed) {
                completedLeadIds.push(String(leadId));
            }
            if (leadAnyOk) {
                checklistSucceededLeadIds.add(String(leadId));
            }
        }
        callIdx += callCount;
    });
    return {
        total: results.length,
        failed: failures.length,
        leadsWithItems,
        totalAutoSkipped,
        totalYesCompleted,
        totalAutoSkipAttempts: autoSkipAttempts.length,
        failedAutoSkipCount,
        failedYesCount,
        completedLeadIds,
        checklistSucceededLeadIds,
        skippedGroups,
        failedItems,
        firstError: failures[0]?.reason?.message,
        // Option 2: expose the already-loaded per-lead task arrays so the caller
        // can select checklist-less standalone tasks WITHOUT a second fetch
        // (each task object carries checklist_items + status + due_at from the
        // detail-fetch in fetchTaskItems).
        leadTasks,
    };
}

async function bulkReopenChecklist(practiceId, leadIds) {
    const leadTasks = await fetchTaskItems(practiceId, leadIds);

    // Per-lead classification:
    //   item-level path  → task has ≥1 YES/SKIPPED item  → reopen those items
    //   task-level path  → task is completed but has NO YES/SKIPPED items
    //                      (cascade-completed via bulk .update, bypassing items)
    //                      → reopen the task directly so checklist_state recomputes
    //   archived tasks   → leave untouched
    const itemCalls = [];  // { promise, kind: 'item' }
    const taskCalls = [];  // { promise, kind: 'task' }
    let leadsWithItems = 0;
    let leadsSkipped = 0;  // leads where every task was archived or had nothing to reopen

    leadTasks.forEach(({ tasks }) => {
        let leadContributed = false;
        tasks.forEach((task) => {
            if (task.status === 'archived') return;

            const terminalItems = (task.checklist_items || []).filter(
                (item) => ['YES', 'SKIPPED'].includes(item.completion_state)
            );

            if (terminalItems.length > 0) {
                // Item-level path: reopen each YES/SKIPPED item individually.
                terminalItems.forEach((item) => {
                    itemCalls.push(
                        callApi(
                            `/api/v1/practices/${practiceId}/tasks/${task.id}/checklist/${item.id}/reopen/`,
                            'POST',
                            {}
                        ).then((res) => {
                            if (!res.ok) throw new Error(apiErrMsg(res));
                            return res;
                        })
                    );
                });
                leadContributed = true;
            } else if (task.status === 'completed') {
                // Fallback task-level path: task was cascade-completed without
                // item-level writes. Reopen the task so checklist_state recomputes.
                taskCalls.push(
                    callApi(
                        `/api/v1/practices/${practiceId}/tasks/${task.id}/reopen/`,
                        'POST',
                        {}
                    ).then((res) => {
                        if (!res.ok) throw new Error(apiErrMsg(res));
                        return res;
                    })
                );
                leadContributed = true;
            }
            // Non-completed, non-archived task with no terminal items: nothing to reopen.
        });
        if (leadContributed) {
            leadsWithItems += 1;
        } else {
            leadsSkipped += 1;
        }
    });

    const allCalls = [...itemCalls, ...taskCalls];
    const results = await Promise.allSettled(allCalls);
    const itemResults = results.slice(0, itemCalls.length);
    const taskResults = results.slice(itemCalls.length);

    const itemFailures = itemResults.filter((r) => r.status === 'rejected');
    const taskFailures = taskResults.filter((r) => r.status === 'rejected');
    const failures = [...itemFailures, ...taskFailures];

    return {
        // total = number of individual reopen calls fired (items + tasks)
        total: allCalls.length,
        failed: failures.length,
        // Split counts for accurate toast copy.
        itemReopened: itemResults.length - itemFailures.length,
        taskReopened: taskResults.length - taskFailures.length,
        leadsWithItems,
        leadsSkipped,
        firstError: failures[0]?.reason?.message,
    };
}

// ── Column (step) bulk action ─────────────────────────────────────────────────

// Classify a single checklist column using the same yes/no-versus-skip contract
// as the whole-checklist Complete action.
function columnActionKind(col) {
    return isBulkYesNoType(col.item_type) ? 'yes' : 'skip';
}

// POST one template step (identified by column.id === item.template_item_id) across
// all selected leads. Reuses fetchTaskItems — no extra API calls.
//   already-terminal (YES/SKIPPED): counted in alreadyDone, not posted.
//   PENDING/PAUSED/IN_PROGRESS: posted with YES or SKIPPED body per column.item_type.
//   lead lacks the step entirely: silent no-op.
async function bulkCompleteColumn(practiceId, leadIds, column, paidRowIds) {
    const leadTasks = await fetchTaskItems(practiceId, leadIds);
    let leadsWithItems = 0;
    let alreadyDone = 0;
    // Per-ROW for PAYMENT_METHOD (paid rows complete, unpaid rows skip), so the
    // column-level flag below is derived from what actually happened rather than
    // assumed from the item type.
    let yesCount = 0;
    let skipCount = 0;
    const calls = [];
    const itemMeta = [];

    leadTasks.forEach(({ leadId, tasks }) => {
        let callCount = 0;
        tasks.forEach((task) => {
            (task.checklist_items || []).forEach((item) => {
                // Guard: only act on the matching template step.
                // String-compare: column.id from API may be string or number.
                if (String(item.template_item_id) !== String(column.id)) return;
                if (!['PENDING', 'PAUSED', 'IN_PROGRESS'].includes(item.completion_state)) {
                    alreadyDone += 1;
                    return;
                }
                const markYes = isBulkYesForItem(column.item_type, leadId, paidRowIds);
                if (markYes) yesCount += 1; else skipCount += 1;
                const itemBody = markYes
                    ? buildCompleteBody(item)
                    : { completion_state: 'SKIPPED', response_payload: { bulk_complete_origin: true } };
                calls.push(
                    callApi(
                        `/api/v1/practices/${practiceId}/tasks/${task.id}/checklist/${item.id}/complete/`,
                        'POST',
                        itemBody
                    ).then((res) => {
                        if (!res.ok) throw new Error(apiErrMsg(res));
                        return res;
                    })
                );
                itemMeta.push({ leadId, itemTitle: item.title || column.title || 'item' });
                callCount += 1;
            });
        });
        if (callCount > 0) leadsWithItems += 1;
    });

    const results = await Promise.allSettled(calls);
    const failures = results.filter((r) => r.status === 'rejected');
    const failedItems = results
        .map((r, idx) => (r.status === 'rejected' ? itemMeta[idx] : null))
        .filter(Boolean);
    return {
        total: results.length,
        failed: failures.length,
        alreadyDone,
        leadsWithItems,
        failedItems,
        // Truthful summary wording: "skipped" only when nothing was actually
        // completed. A payment column across a mixed selection can now do both.
        isSkippedType: yesCount === 0 && skipCount > 0,
        yesCount,
        skipCount,
        firstError: failures[0]?.reason?.message,
    };
}

// ── Lead group helpers ────────────────────────────────────────────────────────

async function bulkAssignLeads(practiceId, leadIds, assigneeIds) {
    const res = await callApi(
        `/api/v1/practices/${practiceId}/leads/bulk_update/?is_archived_included=true`,
        'POST',
        { ids: leadIds.map(Number), field: 'assignees', value: assigneeIds }
    );
    if (res?.status !== 200) throw new Error('Assign failed');
}

async function bulkTagLeads(practiceId, leadIds, tagIds, mode) {
    const endpoint = mode === 'remove' ? 'untag' : 'tag';
    const results = await Promise.allSettled(
        leadIds.map((id) =>
            callApi(
                `/api/v1/practices/${practiceId}/leads/${id}/${endpoint}/`,
                'POST',
                { tag_ids: tagIds }
            ).then((res) => {
                if (!res.ok) throw new Error(apiErrMsg(res));
                return res;
            })
        )
    );
    const failures = results.filter((r) => r.status === 'rejected');
    // bulkTagLeads issues one call per lead, so `total` here equals lead count —
    // but we still expose `total` so the snackbar consumer can use a single
    // consistent format across all bulk helpers.
    return {
        total: results.length,
        failed: failures.length,
        firstError: failures[0]?.reason?.message,
    };
}

// ── Bulk treatment reassignment ───────────────────────────────────────────────
// A lead's treatment lives on its LeadTreatment row, never on the Lead, so this
// re-points the exact LeadTreatment the table's Treatment column is showing — the
// same row the per-lead treatment editor PATCHes. The API writes `treatment` by
// NAME (PracticeTreatmentItemSlugRelatedField resolves it within the practice),
// not by id.
//
// Target resolution, in order:
//   1. a flat row id already carries its LeadTreatment id (Treatment Started /
//      Consultant Attended) — use it verbatim, never a sibling treatment;
//   2. `treatmentTargets` — the lead_id -> treatment_id map the host builds from
//      the rows currently rendered;
//   3. cross-page selections are absent from (2), so ask the API for that lead's
//      treatments, narrowed to the status the table lists.
async function resolveLeadTreatmentId(practiceId, target, treatmentTargets, statusFilter) {
    if (target.leadTreatmentId != null) return target.leadTreatmentId;
    const mapped = treatmentTargets?.[String(target.leadId)];
    if (mapped != null) return mapped;
    const statusQuery = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
    const res = await callApi(`/api/v1/practices/${practiceId}/leads/${target.leadId}/treatments/${statusQuery}`);
    if (!res?.ok) throw new Error(apiErrMsg(res));
    const rows = Array.isArray(res.data) ? res.data : (res.data?.results || []);
    if (!rows.length) throw new Error('Lead has no treatment to reassign');
    return rows[0].id;
}

async function bulkChangeTreatment(practiceId, targets, treatmentName, treatmentTargets, statusFilter) {
    const results = await Promise.allSettled(targets.map(async (target) => {
        const leadTreatmentId = await resolveLeadTreatmentId(
            practiceId, target, treatmentTargets, statusFilter
        );
        const res = await callApi(
            `/api/v1/practices/${practiceId}/leads/${target.leadId}/treatments/${leadTreatmentId}/`,
            'PATCH',
            { treatment: treatmentName },
        );
        if (!res.ok) throw new Error(apiErrMsg(res));
        return res;
    }));
    const failures = results.filter((r) => r.status === 'rejected');
    return {
        total: results.length,
        failed: failures.length,
        firstError: failures[0]?.reason?.message,
    };
}

// ── Reusable confirm dialog ───────────────────────────────────────────────────

function ConfirmDialog({ open, onClose, title, body, onConfirm, loading, confirmLabel = 'Confirm', confirmColor = 'primary' }) {
    return (
        <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>{title}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">{body}</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" disabled={loading} sx={{ flex: 1, borderRadius: 1.5, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button onClick={onConfirm} variant="contained" color={confirmColor} disabled={loading} sx={{ flex: 1, borderRadius: 1.5, textTransform: 'none' }}>
                    {loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
ConfirmDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    body: PropTypes.node.isRequired,
    onConfirm: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    confirmLabel: PropTypes.string,
    confirmColor: PropTypes.string,
};
ConfirmDialog.defaultProps = { loading: false, confirmLabel: 'Confirm', confirmColor: 'primary' };

// ── ActionButton — icon+label on desktop, icon-only on mobile ────────────────

const BTN_COLOR = 'rgba(255,255,255,0.86)';
const BTN_HOVER_BG = 'rgba(255,255,255,0.08)';
const BTN_ACTIVE_BG = 'rgba(255,255,255,0.14)';

function ActionButton({ icon, label, onClick, loading, accentColor, btnRef }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const color = accentColor || BTN_COLOR;

    const btn = (
        <Button
            ref={btnRef}
            variant="text"
            size="small"
            onClick={onClick}
            disabled={loading}
            startIcon={
                loading
                    ? <CircularProgress size={14} sx={{ color }} />
                    : <Iconify icon={icon} width={16} sx={{ color }} />
            }
            sx={{
                color,
                textTransform: 'none',
                minWidth: 'auto',
                px: 1.5,
                '&:hover': { bgcolor: BTN_HOVER_BG },
                '&:active': { bgcolor: BTN_ACTIVE_BG },
            }}
        >
            {!isMobile && label}
        </Button>
    );

    return isMobile ? <Tooltip title={label} placement="top">{btn}</Tooltip> : btn;
}
ActionButton.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    accentColor: PropTypes.string,
    btnRef: PropTypes.any,
};
ActionButton.defaultProps = { loading: false, accentColor: null, btnRef: null };

// ── FloatingBulkActionBar ─────────────────────────────────────────────────────

function FloatingBulkActionBar({ practiceId, onClearSelection, onRefresh, onBulkComplete, onOpenBulkReminder, onItemsSkipped, isArchive, checklistColumns, taskDueBucket, paidRowIds, treatmentTargets, treatmentTargetStatus, nhsOnlyByDefault, practiceByLeadId }) {
    const multiLead = useSelector((state) => state.lead.multiLead);
    const allTags = useSelector((state) => state.tagsCreate?.getAllTag?.results || []);
    const practiceTreatments = useSelector((state) => state.practiceTreatment?.treatmentsList?.results || []);
    const practiceTreatmentsLoading = useSelector((state) => Boolean(state.practiceTreatment?.isLoading));
    const { enqueueSnackbar } = useCustomSnackbar();
    // TCO+USER staff for the assign picker (same scope as the old AssignedToLead
    // staffRole="assignee" call — excludes ADMIN/SUPPORT/superuser).
    usePracticeStaff();

    // DataGrid must retain raw composite row ids for controlled checked state.
    // Every lead API in this bar consumes this one flat-aware conversion boundary.
    const leadIds = getSelectedLeadIdsForApi(multiLead);
    // Task completion is narrower than lead-level archive/tag actions. Flat
    // Treatment Started and Consultant Attended selections retain their exact
    // LeadTreatment identity so a sibling row cannot be mutated or removed.
    const taskTargets = getSelectedTaskTargetsForApi(multiLead);
    const operationScopeKey = [
        String(practiceId ?? ''),
        String(taskDueBucket ?? ''),
        ...taskTargets
            .map(({ rowId, leadId, leadTreatmentId }) => (
                `${String(rowId)}:${String(leadId)}:${String(leadTreatmentId ?? '')}`
            ))
            .sort(),
    ].join('|');
    const mountedRef = useRef(false);
    const operationGenerationRef = useRef(0);
    const activeOperationScopeRef = useRef(operationScopeKey);
    activeOperationScopeRef.current = operationScopeKey;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            operationGenerationRef.current += 1;
        };
    }, []);

    useEffect(() => {
        operationGenerationRef.current += 1;
    }, [operationScopeKey]);

    const beginBulkOperation = useCallback(() => {
        const generation = operationGenerationRef.current + 1;
        operationGenerationRef.current = generation;
        const scopeKey = operationScopeKey;
        const isCurrent = () => (
            mountedRef.current
            && operationGenerationRef.current === generation
            && activeOperationScopeRef.current === scopeKey
        );
        return {
            isCurrent,
            assertCurrent: () => assertOperationCurrent(isCurrent),
        };
    }, [operationScopeKey]);
    const leadCount = leadIds.length;
    const rowTargetCount = taskTargets.length;
    const n = useCallback(
        (word) => `${leadCount} ${word}${leadCount !== 1 ? 's' : ''}`,
        [leadCount]
    );
    const selectedRowsLabel = `${rowTargetCount} selected ${rowTargetCount === 1 ? 'row' : 'rows'}`;
    // Risk 3 fix: hide checklist bulk actions on V1 practices.
    // On V1, the per-checklist endpoints return 403, producing a silent
    // error toast; on V2 with zero tasks selected they return success with
    // no PATCH calls — both produce misleading UX. Gating the buttons
    // entirely on V2 access is the cleanest fix.
    const checklistActionsEnabled = hasFeatureAccess('tasks-v2', practiceId);
    const tasksLabel = (items) => `${items} task${items !== 1 ? 's' : ''}`;
    const leadsWord = (leads) => `${leads} lead${leads !== 1 ? 's' : ''}`;

    const afterAction = useCallback((msg, isOperationCurrent) => {
        assertOperationCurrent(isOperationCurrent);
        // These callbacks form one synchronous UI commit. onClearSelection may
        // intentionally invalidate the captured selection scope, so rechecking
        // between callbacks would cancel the operation's own success handling.
        onClearSelection();
        if (onRefresh) onRefresh();
        enqueueSnackbar(msg, { variant: 'success' });
    }, [onClearSelection, onRefresh, enqueueSnackbar]);

    // Partial bulk archive/restore failure: drop the leads that DID succeed from
    // the kept selection (onBulkComplete prunes Redux + animates the rows out) so
    // a retry only re-sends the failed ids and cannot duplicate notes.
    const pruneSucceededIds = useCallback((info) => {
        const succeededIds = info?.succeededIds;
        if (onBulkComplete && Array.isArray(succeededIds) && succeededIds.length > 0) {
            onBulkComplete(succeededIds);
        }
    }, [onBulkComplete]);

    // ── Mark all done ─────────────────────────────────────────────
    const [doneOpen, setDoneOpen] = useState(false);
    const [doneLoading, setDoneLoading] = useState(false);

    const handleDoneConfirm = async () => {
        const operation = beginBulkOperation();
        setDoneLoading(true);
        try {
            // Phase 1 is read-only. Both resource families must have a complete,
            // stable, scope-checked snapshot for every selected row before any POST
            // or PATCH is created. This prevents a later reminder/task page failure
            // from leaving an earlier channel or lead partially completed.
            const {
                leadTasks: discoveredLeadTasks,
                bucketReminders,
            } = await prepareBulkCompleteDiscovery(
                practiceId,
                taskTargets,
                taskDueBucket,
                operation.isCurrent
            );
            operation.assertCurrent();

            // Phase 2 uses the validated discovery result. bulkChecklistAction must
            // not refetch here because a second read would reopen the validation-to-
            // mutation race that phase 1 closes.
            const checklistResult = await bulkChecklistAction(
                practiceId, taskTargets, 'complete',
                { completion_state: 'YES' },
                operation.isCurrent,
                discoveredLeadTasks,
                paidRowIds
            );
            operation.assertCurrent();

            const {
                total,
                failed,
                totalAutoSkipped,
                totalYesCompleted,
                failedAutoSkipCount,
                failedYesCount,
                checklistSucceededLeadIds,
                skippedGroups,
                failedItems,
                leadTasks,
            } = checklistResult;

            // Complete each bucket-window reminder via PATCH /reminders/{id}/.
            // Uses Promise.allSettled so a failed reminder PATCH never crashes the
            // batch or inflates the success count.  Multi-tenant safety: only the
            // current practice's reminders are fetched (practice-scoped URL) and only
            // the selected leads' reminders are in bucketReminders.
            // Build reminder calls with per-call lead-id tracking so we can compute
            // the union of {leads with ≥1 checklist success} ∪ {leads with ≥1 reminder
            // success} for the "across M leads" toast count (I1 fix — see below).
            const reminderCallMeta = []; // [{ leadId }] — parallel index to reminderCalls
            const reminderCalls = bucketReminders.flatMap(({ leadId, reminders }) =>
                reminders.map((rem) => {
                    operation.assertCurrent();
                    reminderCallMeta.push({ leadId });
                    return callApi(
                        `/api/v1/practices/${practiceId}/reminders/${rem.id}/`,
                        'PATCH',
                        { is_completed: true }
                    ).then((res) => requireTerminalReminderOutcome(res));
                })
            );
            const reminderResults = await Promise.allSettled(reminderCalls);
            operation.assertCurrent();
            const reminderTotal   = reminderCalls.length;
            const reminderFailed  = reminderResults.filter((r) => r.status === 'rejected').length;

            // Option 2: also complete each selected lead's OPEN, checklist-less V2
            // tasks whose due_at is in the current bucket window (reuses the
            // leadTasks ALREADY fetched inside bulkChecklistAction — no second
            // /tasks/ fetch).  Extracted to completeChecklistlessBucketTasks so the
            // exact POST /tasks/{id}/complete/ {force:false} contract, the
            // eligibility guard, and the allSettled failure posture are unit-pinned
            // (the handler calls the REAL function — not a replica).
            const {
                total: standaloneTaskTotal,
                failed: standaloneTaskFailed,
                succeededLeadIds: taskLeadIds,
                failedLeadIds: taskFailedLeadIds,
            } = await completeChecklistlessBucketTasks(
                practiceId,
                leadTasks,
                taskDueBucket,
                undefined,
                operation.isCurrent
            );
            operation.assertCurrent();

            // I1 fix: build "across M leads" as the UNION of:
            //   • leads that had ≥1 successful checklist completion (checklistSucceededLeadIds
            //     from bulkChecklistAction — wider than completedLeadIds, which requires ALL
            //     items to have succeeded AND no hadSkipped)
            //   • leads that had ≥1 successful reminder PATCH (reminderLeadIds)
            //   • leads that had ≥1 successful standalone-task complete (taskLeadIds — Option 2)
            // Math.max is wrong when the sets are disjoint (lead A checklist-only +
            // lead B reminder-only → max=1, union=2).
            const reminderLeadIds = new Set(
                reminderResults
                    .map((r, idx) => (r.status === 'fulfilled' ? String(reminderCallMeta[idx].leadId) : null))
                    .filter(Boolean)
            );
            // Wave 2a: the row-exit / prune set must be the UNION of every lead that
            // had ≥1 successful completion across ALL three channels — checklist,
            // bucket-window reminder (A′), and checklist-less standalone task (Option 2).
            // Previously only `completedLeadIds` (checklist-only) was fed to onBulkComplete,
            // so a lead completed purely via a reminder or standalone task lingered in the
            // table until the 6.5s reconcile refetch and stayed selected. Keep the ids as
            // strings so the parent's prune (String-keyed) matches regardless of numeric
            // vs string lead ids.
            const succeededUnionLeadIds = buildUnionLeadIds({
                checklistLeadIds: checklistSucceededLeadIds,
                reminderLeadIds,
                taskLeadIds,
            });
            const failedLeadIds = new Set([
                ...failedItems.map(({ leadId }) => String(leadId)),
                ...reminderResults
                    .map((r, idx) => (r.status === 'rejected' ? String(reminderCallMeta[idx].leadId) : null))
                    .filter(Boolean),
                ...taskFailedLeadIds,
            ]);
            // Keep a lead selected for retry if any attempted mutation failed.
            const unionLeadIds = succeededUnionLeadIds.filter(
                (leadId) => !failedLeadIds.has(String(leadId))
            );
            const unionLeadCount = countCanonicalLeads(succeededUnionLeadIds);

            operation.assertCurrent();
            setDoneOpen(false);

            // Surface the informational skip dialog in the parent for every group
            // that bulk Complete terminalized server-side as SKIPPED.
            const hasSkipped = Object.values(skippedGroups).some((ids) => ids.length > 0);
            if (hasSkipped && onItemsSkipped) {
                operation.assertCurrent();
                onItemsSkipped(skippedGroups);
            }

            const hasAnyWork = total > 0 || reminderTotal > 0 || standaloneTaskTotal > 0;

            if (!hasAnyWork) {
                // Risk 3 fix: no checklist items, bucket-window reminders, or
                // checklist-less standalone tasks existed on any selected lead.  Was
                // previously showing "${count} leads marked complete" — false success.
                operation.assertCurrent();
                enqueueSnackbar(
                    'No tasks or reminders found on selected leads.',
                    { variant: 'info' }
                );
                operation.assertCurrent();
                onClearSelection();
            } else if (failed === 0 && reminderFailed === 0 && standaloneTaskFailed === 0) {
                // All items succeeded: drive animation + counter refresh via onBulkComplete.
                // onBulkComplete handles: refreshCounters (badge update) + per-lead exit
                // animation + optimistic row removal + 6.5 s reconcile refetch.
                //
                // Honest count: LEAD_NEXT_ACTION items were auto-SKIPPED (terminal state),
                // not YES-completed. Show the split so the user knows what happened.
                const taskSucceeded = totalYesCompleted;
                // Wave 2c: when a bucket is active (tasks mode), only items DUE in the
                // current view were completed — a lead may still have later-dated items.
                // Tell the rep so they don't assume the lead is fully cleared.
                const bucketScopeNote = taskDueBucket
                    ? ' Only items due in this view were completed.'
                    : '';
                const taskSkipNote = totalAutoSkipped > 0
                    ? ` ${totalAutoSkipped} non-yes/no item${totalAutoSkipped === 1 ? '' : 's'} marked skipped.${bucketScopeNote}`
                    : bucketScopeNote;
                const toastMsg = buildBulkCompleteToast({
                    taskTotal:      taskSucceeded,
                    taskFailed:     0,
                    standaloneTaskTotal,
                    standaloneTaskFailed: 0,
                    reminderTotal,
                    reminderFailed: 0,
                    leadsWithItems: unionLeadCount,
                    taskSkipNote,
                });
                // One guard covers this synchronous success commit. Clearing
                // selection intentionally invalidates the operation scope.
                operation.assertCurrent();
                onClearSelection();
                // Wave 2c: longer dwell — the success toast carries counts + a scope note
                // the rep needs time to read (was the notistack 5s default, too transient).
                enqueueSnackbar(toastMsg, { variant: 'success', autoHideDuration: 7000 });
                emitTasksV2Changed();
                // Wave 2a: animate out / prune the UNION (checklist ∪ reminder ∪ standalone),
                // not just the checklist-only ids. onClearSelection already cleared the whole
                // selection on full success; onBulkComplete drives the per-lead exit animation.
                if (onBulkComplete) {
                    onBulkComplete(unionLeadIds);
                }
            } else {
                // At least one checklist, reminder, or standalone-task mutation failed.
                // Route through buildBulkCompleteToast for consistent phrasing.
                // P1: append the named item failures so the rep knows which items to fix.
                const namedFailures = failedItems.slice(0, 2).map((f) => `"${f.itemTitle}"`).join(', ');
                const moreCount = failedItems.length > 2 ? ` (+${failedItems.length - 2} more)` : '';
                const namedDetail = failed > 0
                    ? ` Couldn't update: ${namedFailures}${moreCount}.`
                    : '';
                const successfulSkipNote = totalAutoSkipped > 0
                    ? ` ${totalAutoSkipped} non-yes/no item${totalAutoSkipped === 1 ? '' : 's'} marked skipped.`
                    : '';
                const failedSkipNote = failedAutoSkipCount > 0
                    ? ` ${failedAutoSkipCount} non-yes/no item${failedAutoSkipCount === 1 ? '' : 's'} could not be skipped and remain available to retry.`
                    : '';
                const partialToast = buildBulkCompleteToast({
                    taskTotal:      totalYesCompleted + failedYesCount,
                    taskFailed:     failedYesCount,
                    standaloneTaskTotal,
                    standaloneTaskFailed,
                    reminderTotal,
                    reminderFailed,
                    leadsWithItems: unionLeadCount,
                    taskSkipNote: `${successfulSkipNote}${failedSkipNote}${namedDetail}`,
                });
                // Wave 2c: partial toast carries named failures + retry guidance — give
                // it a longer dwell than the success toast so the rep can act on it.
                operation.assertCurrent();
                enqueueSnackbar(partialToast, { variant: 'warning', autoHideDuration: 10000 });
                operation.assertCurrent();
                emitTasksV2Changed();
                if (onRefresh) {
                    operation.assertCurrent();
                    onRefresh();
                }
                // Partial: prune ONLY the leads that fully succeeded (the union set),
                // leaving leads with a failed item still selected so the rep can retry
                // without re-selecting. onBulkComplete prunes those ids from Redux (and,
                // because selection is controlled, unticks their checkboxes). We do NOT
                // call onClearSelection here — that would wipe the retry set too.
                if (onBulkComplete && unionLeadIds.length > 0) {
                    operation.assertCurrent();
                    onBulkComplete(unionLeadIds);
                }
            }
        } catch (err) {
            if (isBulkOperationSuperseded(err) || !operation.isCurrent()) return;
            const errorMessage = err?.message || '';
            if (/reminders/i.test(errorMessage) && /fetch|response|pagination|scope|changed/i.test(errorMessage)) {
                enqueueSnackbar(
                    "Couldn't load reminders for selected leads. Please retry.",
                    { variant: 'warning' }
                );
            } else {
                const msg = /^Failed to fetch task/i.test(errorMessage)
                    ? "Couldn't load tasks for selected leads. Please retry."
                    : `Bulk complete failed — ${errorMessage || 'please try again.'}`;
                dispatch(updateError({ success: false, message: msg }));
            }
        } finally {
            if (operation.isCurrent()) setDoneLoading(false);
        }
    };

    // ── Skip all ──────────────────────────────────────────────────
    const [skipOpen, setSkipOpen] = useState(false);
    const [skipLoading, setSkipLoading] = useState(false);

    useEffect(() => {
        setDoneLoading(false);
        setSkipLoading(false);
    }, [operationScopeKey]);

    const handleSkipConfirm = async () => {
        const operation = beginBulkOperation();
        setSkipLoading(true);
        try {
            const { total, failed, leadsWithItems, firstError } = await bulkChecklistAction(
                practiceId, taskTargets, 'complete',
                // Empty response_payload — no discriminator key on manual bulk skip.
                // bulk_complete_origin: true is reserved for the auto-skip path above.
                // Absence of bulk_complete_origin = manual skip (no answer:'manual' — not in the
                // LEAD_NEXT_ACTION payload schema and was triggering a Sentry warn).
                { completion_state: 'SKIPPED', response_payload: {} },
                operation.isCurrent
            );
            operation.assertCurrent();
            setSkipOpen(false);
            if (total === 0) {
                operation.assertCurrent();
                enqueueSnackbar('No checklist tasks found on selected leads.', { variant: 'info' });
                operation.assertCurrent();
                onClearSelection();
            } else if (failed === 0) {
                operation.assertCurrent();
                emitTasksV2Changed();
                afterAction(
                    `${tasksLabel(total)} skipped across ${leadsWord(leadsWithItems)}.`,
                    operation.isCurrent
                );
            } else {
                // The callbacks below are one synchronous partial-result commit.
                operation.assertCurrent();
                enqueueSnackbar(`Skip — ${total - failed} of ${total} items succeeded, ${failed} failed${firstError ? `: ${firstError}` : ''}`, { variant: 'warning' });
                onClearSelection();
                emitTasksV2Changed();
                if (onRefresh) {
                    onRefresh();
                }
            }
        } catch (err) {
            if (isBulkOperationSuperseded(err) || !operation.isCurrent()) return;
            const msg = /^Failed to fetch task/i.test(err?.message || '')
                ? "Couldn't load tasks for selected leads. Please retry."
                : `Bulk skip failed — ${err?.message || 'please try again.'}`;
            dispatch(updateError({ success: false, message: msg }));
        } finally {
            if (operation.isCurrent()) setSkipLoading(false);
        }
    };

    // ── Pause all ─────────────────────────────────────────────────
    const [pauseOpen, setPauseOpen] = useState(false);
    const [pauseDate, setPauseDate] = useState('');
    const [pauseLoading, setPauseLoading] = useState(false);

    const handlePauseConfirm = async () => {
        if (!pauseDate) return;
        setPauseLoading(true);
        try {
            const { total, failed, leadsWithItems, firstError } = await bulkChecklistAction(
                practiceId, leadIds, 'pause',
                { paused_until: new Date(pauseDate).toISOString() }
            );
            setPauseOpen(false);
            const dateLabel = new Date(pauseDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
            setPauseDate('');
            if (total === 0) {
                enqueueSnackbar('No checklist tasks found on selected leads.', { variant: 'info' });
                onClearSelection();
            } else if (failed === 0) {
                emitTasksV2Changed();
                afterAction(`${tasksLabel(total)} paused until ${dateLabel} across ${leadsWord(leadsWithItems)}.`);
            } else {
                enqueueSnackbar(`Pause — ${total - failed} of ${total} items succeeded, ${failed} failed${firstError ? `: ${firstError}` : ''}`, { variant: 'warning' });
                onClearSelection();
                emitTasksV2Changed();
                if (onRefresh) onRefresh();
            }
        } catch (err) {
            const msg = /^Failed to fetch task/i.test(err?.message || '')
                ? "Couldn't load tasks for selected leads. Please retry."
                : `Bulk pause failed — ${err?.message || 'please try again.'}`;
            dispatch(updateError({ success: false, message: msg }));
        } finally {
            setPauseLoading(false);
        }
    };

    // ── Re-open all ───────────────────────────────────────────────
    const [reopenOpen, setReopenOpen] = useState(false);
    const [reopenLoading, setReopenLoading] = useState(false);

    const handleReopenConfirm = async () => {
        setReopenLoading(true);
        try {
            const { total, failed, itemReopened, taskReopened, leadsWithItems, leadsSkipped, firstError } =
                await bulkReopenChecklist(practiceId, leadIds);
            setReopenOpen(false);
            if (total === 0) {
                // No completed/skipped items found and no cascade-completed tasks.
                const skippedNote = leadsSkipped > 0
                    ? ` (${leadsWord(leadsSkipped)} had nothing to reopen)`
                    : '';
                enqueueSnackbar(
                    `No completed or skipped checklist tasks found on selected leads.${skippedNote}`,
                    { variant: 'info' }
                );
                onClearSelection();
            } else if (failed === 0) {
                // Full success — build a clear breakdown of what was reopened.
                const parts = [];
                if (itemReopened > 0) parts.push(`${itemReopened} checklist item${itemReopened !== 1 ? 's' : ''}`);
                if (taskReopened > 0) parts.push(`${taskReopened} task${taskReopened !== 1 ? 's' : ''} (cascade-completed)`);
                const skippedNote = leadsSkipped > 0
                    ? ` — ${leadsWord(leadsSkipped)} had nothing to reopen`
                    : '';
                emitTasksV2Changed();
                afterAction(`Reopened ${parts.join(' + ')} across ${leadsWord(leadsWithItems)}.${skippedNote}`);
            } else {
                const succeeded = total - failed;
                enqueueSnackbar(
                    `Reopen — ${succeeded} of ${total} succeeded, ${failed} failed${firstError ? `: ${firstError}` : ''}`,
                    { variant: 'warning' }
                );
                onClearSelection();
                emitTasksV2Changed();
                if (onRefresh) onRefresh();
            }
        } catch (err) {
            const msg = /^Failed to fetch task/i.test(err?.message || '')
                ? "Couldn't load tasks for selected leads. Please retry."
                : `Bulk reopen failed — ${err?.message || 'please try again.'}`;
            dispatch(updateError({ success: false, message: msg }));
        } finally {
            setReopenLoading(false);
        }
    };

    // ── Column (step) picker ──────────────────────────────────────
    const columnBtnRef = useRef(null);
    const [columnPickerOpen, setColumnPickerOpen] = useState(false);
    const [columnPickerLoading, setColumnPickerLoading] = useState(false);

    // Build annotated column entries once per checklistColumns change.
    const annotatedColumns = useMemo(() => (checklistColumns || []).map((col) => ({
        ...col,
        _actionKind: columnActionKind(col),
    })), [checklistColumns]);

    const handleColumnSelect = async (col) => {
        setColumnPickerOpen(false);
        setColumnPickerLoading(true);
        try {
            const { total, failed, alreadyDone, leadsWithItems, failedItems, isSkippedType, firstError } =
                await bulkCompleteColumn(practiceId, leadIds, col, paidRowIds);
            const verb = isSkippedType ? 'skipped' : 'completed';
            const doneNote = alreadyDone > 0 ? `, ${alreadyDone} already done` : '';
            if (total === 0 && alreadyDone === 0) {
                enqueueSnackbar('No matching checklist items found on selected leads.', { variant: 'info' });
                onClearSelection();
            } else if (total === 0) {
                enqueueSnackbar('All matching items already done.', { variant: 'info' });
                onClearSelection();
            } else if (failed === 0) {
                emitTasksV2Changed();
                afterAction(`${col.title} — ${tasksLabel(total)} ${verb}${doneNote} across ${leadsWord(leadsWithItems)}.`);
            } else {
                const namedFailures = failedItems.slice(0, 2).map((f) => `"${f.itemTitle}"`).join(', ');
                const moreCount = failedItems.length > 2 ? ` (+${failedItems.length - 2} more)` : '';
                enqueueSnackbar(
                    `${col.title} — ${total - failed} of ${total} ${verb}. Couldn't complete: ${namedFailures}${moreCount}${firstError ? ` (${firstError})` : ''}.`,
                    { variant: 'warning' }
                );
                onClearSelection();
                emitTasksV2Changed();
                if (onRefresh) onRefresh();
            }
        } catch (err) {
            const msg = /^Failed to fetch task/i.test(err?.message || '')
                ? "Couldn't load tasks for selected leads. Please retry."
                : `Bulk column action failed — ${err?.message || 'please try again.'}`;
            dispatch(updateError({ success: false, message: msg }));
        } finally {
            setColumnPickerLoading(false);
        }
    };

    // ── Assign ────────────────────────────────────────────────────
    const assignBtnRef = useRef(null);
    const [assignOpen, setAssignOpen] = useState(false);
    const [assignees, setAssignees] = useState([]);
    const [assignLoading, setAssignLoading] = useState(false);

    const handleAssignApply = async () => {
        if (assignees.length === 0) return;
        setAssignLoading(true);
        try {
            await bulkAssignLeads(practiceId, leadIds, assignees.map((a) => a.id));
            setAssignOpen(false);
            setAssignees([]);
            afterAction(`${n('lead')} assigned to ${assignees.map((a) => a._name).join(', ')}`);
        } catch {
            dispatch(updateError({ success: false, message: 'Bulk assign failed — please try again.' }));
        } finally {
            setAssignLoading(false);
        }
    };

    // ── Tag ───────────────────────────────────────────────────────
    const tagBtnRef = useRef(null);
    const [tagOpen, setTagOpen] = useState(false);
    const [tagMode, setTagMode] = useState('add');
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [tagLoading, setTagLoading] = useState(false);

    // Fetch tag list when popover opens
    useEffect(() => {
        if (tagOpen && allTags.length === 0) {
            dispatch(getAllTagItem(practiceId, 100, 0));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tagOpen, practiceId]);

    const handleTagToggle = (tagId) => {
        setSelectedTagIds((prev) =>
            prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
        );
    };

    const handleTagApply = async () => {
        if (selectedTagIds.length === 0) return;
        setTagLoading(true);
        try {
            const { total, failed } = await bulkTagLeads(practiceId, leadIds, selectedTagIds, tagMode);
            setTagOpen(false);
            setSelectedTagIds([]);
            const verb = tagMode === 'remove' ? 'untagged' : 'tagged';
            if (failed === 0) {
                afterAction(`${n('lead')} ${verb}`);
            } else {
                enqueueSnackbar(`${verb} — ${total - failed} of ${total} leads succeeded, ${failed} failed`, { variant: 'warning' });
                onClearSelection();
                if (onRefresh) onRefresh();
            }
        } catch {
            dispatch(updateError({ success: false, message: 'Bulk tag failed — please try again.' }));
        } finally {
            setTagLoading(false);
        }
    };

    // ── Treatment (assign / reassign) ─────────────────────────────
    // Only offered when the host supplies `treatmentTargets`; without that map the
    // bar cannot know WHICH of a lead's treatments the table is showing, and
    // guessing one would silently rewrite the wrong row.
    const treatmentBtnRef = useRef(null);
    const [treatmentOpen, setTreatmentOpen] = useState(false);
    const [treatmentChoice, setTreatmentChoice] = useState(null);
    const [treatmentLoading, setTreatmentLoading] = useState(false);
    const [treatmentQuery, setTreatmentQuery] = useState('');
    const [nhsOnly, setNhsOnly] = useState(Boolean(nhsOnlyByDefault));
    const treatmentActionEnabled = Boolean(treatmentTargets) && !isArchive;
    // Category-sorted so the grouped list emits each category header exactly once.
    const treatmentOptions = useMemo(() => (
        [...FilterTreatmentList(practiceTreatments)].sort((a, b) => {
            const ca = (a?.category || '').toUpperCase();
            const cb = (b?.category || '').toUpperCase();
            if (ca !== cb) return ca < cb ? -1 : 1;
            const na = (a?.name || '').toUpperCase();
            const nb = (b?.name || '').toUpperCase();
            // eslint-disable-next-line no-nested-ternary
            return na < nb ? -1 : (na > nb ? 1 : 0);
        })
    ), [practiceTreatments]);

    const nhsTreatmentCount = useMemo(
        () => treatmentOptions.filter(isNhsTreatment).length,
        [treatmentOptions]
    );
    // The toggle is the user's intent; this is whether it can actually apply. A
    // practice with no NHS-named treatments would otherwise open to an empty list.
    const nhsFilterActive = nhsOnly && nhsTreatmentCount > 0;

    const visibleTreatments = useMemo(() => {
        const q = treatmentQuery.trim().toLowerCase();
        return treatmentOptions.filter((option) => {
            if (nhsFilterActive && !isNhsTreatment(option)) return false;
            if (!q) return true;
            return `${option?.name || ''} ${option?.category || ''}`.toLowerCase().includes(q);
        });
    }, [treatmentOptions, nhsFilterActive, treatmentQuery]);

    // Fetch the practice's treatment list when the popover opens. limit:30 byte-matches
    // the limit the lead tables already fetch with, so callApi's in-flight dedupe
    // collapses this into their request rather than issuing a second one.
    useEffect(() => {
        if (treatmentOpen && practiceId && practiceTreatments.length === 0) {
            dispatch(getPracticeTreatmentList(practiceId, { limit: 30, offset: 0, type: 'searchTreatment' }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [treatmentOpen, practiceId]);

    // Each open starts clean and back at the host's default, so a filter left on from
    // a previous use can never silently hide treatments on the next one.
    useEffect(() => {
        if (!treatmentOpen) return;
        setTreatmentQuery('');
        setTreatmentChoice(null);
        setNhsOnly(Boolean(nhsOnlyByDefault));
    }, [treatmentOpen, nhsOnlyByDefault]);

    // Flipping the filter must not leave a now-hidden treatment armed behind the
    // Apply button. Search is transient and deliberately does NOT clear the choice.
    const handleNhsOnlyChange = (checked) => {
        setNhsOnly(checked);
        setTreatmentChoice((current) => (
            current && checked && !isNhsTreatment(current) ? null : current
        ));
    };

    const handleTreatmentApply = async () => {
        if (!treatmentChoice?.name) return;
        setTreatmentLoading(true);
        try {
            const { total, failed, firstError } = await bulkChangeTreatment(
                practiceId, taskTargets, treatmentChoice.name, treatmentTargets, treatmentTargetStatus
            );
            setTreatmentOpen(false);
            const chosen = treatmentChoice.name;
            setTreatmentChoice(null);
            if (failed === 0) {
                afterAction(`${n('lead')} moved to ${chosen}`);
            } else {
                enqueueSnackbar(
                    `Treatment changed on ${total - failed} of ${total} — ${failed} failed${firstError ? ` (${firstError})` : ''}`,
                    { variant: 'warning' }
                );
                onClearSelection();
                if (onRefresh) onRefresh();
            }
        } catch {
            dispatch(updateError({ success: false, message: 'Bulk treatment change failed — please try again.' }));
        } finally {
            setTreatmentLoading(false);
        }
    };

    // ── Archive ───────────────────────────────────────────────────
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [archiveReason, setArchiveReason] = useState('');
    const [archiveLoading, setArchiveLoading] = useState(false);

    const handleArchiveConfirm = useCallback(() => {
        if (!archiveReason) return;
        setArchiveLoading(true);
        dispatch(
            bulkArchivedOrRestore(
                practiceId,
                { object_ids: leadIds, request_type: 'ARCHIVE', reason: archiveReason },
                (status, info) => {
                    setArchiveLoading(false);
                    if (status?.toLowerCase?.() === 'finish' || status === undefined) {
                        setArchiveOpen(false);
                        setArchiveReason('');
                        afterAction(`${n('lead')} archived`);
                    } else if (status?.toLowerCase?.() === 'error') {
                        pruneSucceededIds(info);
                    }
                },
                practiceByLeadId
            )
        );
    }, [practiceId, leadIds, archiveReason, afterAction, n, practiceByLeadId, pruneSucceededIds]);

    // ── Restore (archive page only) ───────────────────────────────
    const [restoreOpen, setRestoreOpen] = useState(false);
    const [restoreLoading, setRestoreLoading] = useState(false);

    // ── Change communication (archive page only) ──────────────────
    // Reuses BulkCommunicationPopup (Yes/No "Open to communication" toggle);
    // it reads the selected leads from redux and posts the bulk_update itself.
    const [commOpen, setCommOpen] = useState(false);

    const handleRestoreConfirm = useCallback(() => {
        setRestoreLoading(true);
        dispatch(
            bulkArchivedOrRestore(
                practiceId,
                { object_ids: leadIds, request_type: 'RESTORE', note: 'Bulk restored from archive table' },
                (status, info) => {
                    setRestoreLoading(false);
                    if (status?.toLowerCase?.() === 'finish' || status === undefined) {
                        setRestoreOpen(false);
                        afterAction(`${n('lead')} restored`);
                    } else if (status?.toLowerCase?.() === 'error') {
                        pruneSucceededIds(info);
                    }
                },
                practiceByLeadId
            )
        );
    }, [practiceId, leadIds, afterAction, n, practiceByLeadId, pruneSucceededIds]);

    // ── Render ────────────────────────────────────────────────────
    const bar = (
        <AnimatePresence>
            {rowTargetCount > 0 && (
                <m.div
                    key="bulk-bar"
                    initial={{ y: 40, opacity: 0, scale: 0.98 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: 32, opacity: 0 }}
                    transition={{
                        default: { type: 'spring', stiffness: 380, damping: 28 },
                        exit: { duration: 0.18, ease: 'easeIn' },
                    }}
                    style={{
                        position: 'fixed',
                        bottom: 24,
                        left: 0,
                        right: 0,
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        zIndex: 1400,
                        // Size to the action set (the archive bar carries extra
                        // Communication + Restore actions and was overflowing the
                        // old fixed 920px cap), but never exceed the viewport.
                        width: 'fit-content',
                        maxWidth: 'calc(100vw - 48px)',
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            display: 'flex',
                            // On very narrow screens scroll the actions instead of
                            // letting them spill outside the bar.
                            maxWidth: '100%',
                            overflowX: 'auto',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1.25,
                            bgcolor: 'rgba(20, 22, 28, 0.85)',
                            backdropFilter: 'blur(20px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: 3.5,
                            boxShadow: '0 16px 48px rgba(0,0,0,0.32), 0 2px 8px rgba(0,0,0,0.16)',
                            color: '#fff',
                            minHeight: 56,
                        }}
                    >
                        {/* Count chip — pulses on count change */}
                        <m.div key={rowTargetCount} animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 0.2 }} style={{ flexShrink: 0 }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                sx={{ px: 1.25, py: 0.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.10)' }}
                            >
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#fff', lineHeight: 1 }} noWrap>
                                    {rowTargetCount} selected
                                </Typography>
                            </Stack>
                        </m.div>

                        <Divider orientation="vertical" sx={{ height: 24, bgcolor: 'rgba(255,255,255,0.12)', mx: 0.5 }} />

                        {/* Checklist group — V2-only (Risk 3 fix).
                            On V1 practices these endpoints 403; on V2 with zero tasks
                            on selected leads they succeed-silently. Hide entirely when
                            tasks-v2 is not enabled. Reminder stays visible since it
                            isn't tied to the checklist endpoints. */}
                        {checklistActionsEnabled && (
                            <>
                                <ActionButton icon="eva:checkmark-circle-2-fill" label="Complete" onClick={() => setDoneOpen(true)} loading={doneLoading} accentColor="#36b37e" />
                                <ActionButton icon="eva:close-circle-outline" label="Skip" onClick={() => setSkipOpen(true)} loading={skipLoading} />
                                <ActionButton icon="eva:clock-outline" label="Pause" onClick={() => setPauseOpen(true)} loading={pauseLoading} />
                                {annotatedColumns.length > 0 && (
                                    <ActionButton
                                        icon="eva:grid-outline"
                                        label="Column"
                                        onClick={() => setColumnPickerOpen((p) => !p)}
                                        loading={columnPickerLoading}
                                        btnRef={columnBtnRef}
                                    />
                                )}
                            </>
                        )}
                        {onOpenBulkReminder && <ActionButton icon="eva:bell-outline" label="Reminder" onClick={onOpenBulkReminder} />}
                        {checklistActionsEnabled && (
                            <ActionButton icon="eva:undo-outline" label="Reopen" onClick={() => setReopenOpen(true)} loading={reopenLoading} />
                        )}

                        <Divider orientation="vertical" sx={{ height: 24, bgcolor: 'rgba(255,255,255,0.12)', mx: 0.5 }} />

                        {/* Lead group */}
                        <ActionButton icon="eva:person-add-outline" label="Assign" onClick={() => setAssignOpen((p) => !p)} loading={assignLoading} btnRef={assignBtnRef} />
                        <ActionButton icon="eva:pricetags-outline" label="Tag" onClick={() => setTagOpen((p) => !p)} loading={tagLoading} btnRef={tagBtnRef} />
                        {treatmentActionEnabled && (
                            <ActionButton icon="eva:swap-outline" label="Treatment" onClick={() => setTreatmentOpen((p) => !p)} loading={treatmentLoading} btnRef={treatmentBtnRef} />
                        )}
                        {isArchive ? (
                            <>
                                <ActionButton icon="eva:message-circle-outline" label="Communication" onClick={() => setCommOpen(true)} />
                                <ActionButton icon="eva:flip-2-outline" label="Restore" onClick={() => setRestoreOpen(true)} loading={restoreLoading} accentColor="#36b37e" />
                            </>
                        ) : (
                            <ActionButton icon="eva:archive-outline" label="Archive" onClick={() => setArchiveOpen(true)} loading={archiveLoading} accentColor="#ff5630" />
                        )}

                        <Divider orientation="vertical" sx={{ height: 24, bgcolor: 'rgba(255,255,255,0.12)', mx: 0.5 }} />

                        {/* Clear — labeled + high-contrast (was a faint 16px rgba(.5) X that
                            reps couldn't find). Explicit "Clear" text next to the actions so
                            deselecting is discoverable. Handler unchanged (onClearSelection);
                            now effective because selection is controlled by Redux. */}
                        <Button
                            size="small"
                            onClick={onClearSelection}
                            startIcon={<Iconify icon="eva:close-fill" width={16} />}
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                textTransform: 'none',
                                fontSize: 13,
                                fontWeight: 600,
                                borderRadius: 1.5,
                                px: 1.25,
                                '&:hover': { color: '#fff', bgcolor: BTN_HOVER_BG },
                            }}
                        >
                            Clear
                        </Button>
                    </Paper>

                    {/* Assign popover */}
                    <Popper open={assignOpen} anchorEl={assignBtnRef.current} placement="top" modifiers={BAR_POPPER_MODIFIERS} style={{ zIndex: 1500 }}>
                        <ClickAwayListener onClickAway={() => setAssignOpen(false)}>
                            <Paper elevation={0} sx={{ ...barPopoverPaperSx, minWidth: 260 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Assign to</Typography>
                                <AssignedToLead
                                    staffRole="assignee"
                                    assignToState={assignees}
                                    setAssignTo={setAssignees}
                                    showSelectAll
                                />
                                <Button
                                    fullWidth variant="contained" size="small"
                                    disabled={assignees.length === 0 || assignLoading}
                                    onClick={handleAssignApply}
                                    startIcon={assignLoading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
                                    sx={{ mt: 2, ...NO_CAPS, borderRadius: 1.5, bgcolor: '#00A76F', '&:hover': { bgcolor: '#007B52' } }}
                                >
                                    Apply to {leadCount} lead{leadCount !== 1 ? 's' : ''}
                                </Button>
                            </Paper>
                        </ClickAwayListener>
                    </Popper>

                    {/* Tag popover */}
                    <Popper open={tagOpen} anchorEl={tagBtnRef.current} placement="top" modifiers={BAR_POPPER_MODIFIERS} style={{ zIndex: 1500 }}>
                        <ClickAwayListener onClickAway={() => setTagOpen(false)}>
                            <Paper elevation={0} sx={{ ...barPopoverPaperSx, minWidth: 260, maxWidth: 320 }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                    <Typography variant="subtitle2">Tags</Typography>
                                    <ToggleButtonGroup
                                        size="small"
                                        exclusive
                                        value={tagMode}
                                        onChange={(_, v) => v && setTagMode(v)}
                                        sx={{ height: 28 }}
                                    >
                                        <ToggleButton value="add" sx={{ px: 1.5, fontSize: 11, textTransform: 'none' }}>Add</ToggleButton>
                                        <ToggleButton value="remove" sx={{ px: 1.5, fontSize: 11, textTransform: 'none' }}>Remove</ToggleButton>
                                    </ToggleButtonGroup>
                                </Stack>
                                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ maxHeight: 160, overflowY: 'auto', mb: 1.5 }}>
                                    {allTags.map((tag) => {
                                        const sel = selectedTagIds.includes(tag.id);
                                        return (
                                            <Button
                                                key={tag.id}
                                                size="small"
                                                variant={sel ? 'contained' : 'outlined'}
                                                onClick={() => handleTagToggle(tag.id)}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontSize: 11,
                                                    borderRadius: 3,
                                                    py: 0.25,
                                                    px: 1,
                                                    borderColor: tag.tag_colour || 'divider',
                                                    color: sel ? '#fff' : (tag.tag_colour || 'text.primary'),
                                                    bgcolor: sel ? (tag.tag_colour || 'primary.main') : 'transparent',
                                                    '&:hover': { bgcolor: tag.tag_colour ? `${tag.tag_colour}22` : undefined },
                                                }}
                                            >
                                                {tag.name}
                                            </Button>
                                        );
                                    })}
                                    {allTags.length === 0 && (
                                        <Typography variant="caption" color="text.disabled">No tags found</Typography>
                                    )}
                                </Stack>
                                <Button
                                    fullWidth variant="contained" size="small"
                                    disabled={selectedTagIds.length === 0 || tagLoading}
                                    onClick={handleTagApply}
                                    startIcon={tagLoading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
                                    sx={{ ...NO_CAPS, borderRadius: 1.5 }}
                                >
                                    {tagMode === 'remove' ? 'Remove' : 'Add'} tags — {leadCount} lead{leadCount !== 1 ? 's' : ''}
                                </Button>
                            </Paper>
                        </ClickAwayListener>
                    </Popper>

                    {/* Treatment popover.
                        The list is INLINE rather than an Autocomplete popup: the bar sits at
                        the bottom of the viewport, so a popup listbox flips upward and renders
                        over this panel's own header. An inline list also shows the options
                        without a click and gives the NHS filter somewhere to live. */}
                    <Popper
                        open={treatmentOpen && treatmentActionEnabled}
                        anchorEl={treatmentBtnRef.current}
                        placement="top"
                        modifiers={BAR_POPPER_MODIFIERS}
                        style={{ zIndex: 1500 }}
                    >
                        <ClickAwayListener onClickAway={() => setTreatmentOpen(false)}>
                            <Paper elevation={0} sx={{ ...barPopoverPaperSx, width: 340, p: 0, overflow: 'hidden' }}>
                                <Box sx={{ p: 2, pb: 1.25 }}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                        <Typography variant="subtitle2">Change treatment</Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                px: 1, py: 0.25, borderRadius: 1,
                                                bgcolor: 'rgba(145, 158, 171, 0.12)',
                                                color: 'text.secondary', fontWeight: 600,
                                            }}
                                        >
                                            {leadCount} lead{leadCount !== 1 ? 's' : ''}
                                        </Typography>
                                    </Stack>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        // eslint-disable-next-line jsx-a11y/no-autofocus
                                        autoFocus
                                        value={treatmentQuery}
                                        onChange={(e) => setTreatmentQuery(e.target.value)}
                                        placeholder="Search treatments…"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Iconify icon="eva:search-outline" width={18} sx={{ color: 'text.disabled' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                                    />
                                    {nhsTreatmentCount > 0 && (
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                NHS treatments only
                                            </Typography>
                                            <Switch
                                                size="small"
                                                checked={nhsOnly}
                                                onChange={(e) => handleNhsOnlyChange(e.target.checked)}
                                                inputProps={{ 'aria-label': 'Show NHS treatments only' }}
                                            />
                                        </Stack>
                                    )}
                                </Box>

                                <Box sx={{ maxHeight: 240, overflowY: 'auto', borderTop: '1px solid rgba(145, 158, 171, 0.16)' }}>
                                    {practiceTreatmentsLoading && treatmentOptions.length === 0 && (
                                        <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>
                                            Loading treatments…
                                        </Typography>
                                    )}
                                    {!practiceTreatmentsLoading && visibleTreatments.length === 0 && (
                                        <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>
                                            {nhsFilterActive
                                                ? 'No NHS treatment matches — turn off the NHS filter to see the rest.'
                                                : 'No matching treatment.'}
                                        </Typography>
                                    )}
                                    {visibleTreatments.map((option, index) => {
                                        const category = option.category || 'Other';
                                        const isFirstOfCategory = index === 0
                                            || (visibleTreatments[index - 1].category || 'Other') !== category;
                                        const isSelected = treatmentChoice?.id === option.id;
                                        return (
                                            <Box key={option.id}>
                                                {isFirstOfCategory && (
                                                    <ListSubheader
                                                        disableSticky
                                                        sx={{
                                                            px: 2, py: 0.5, lineHeight: 1.8,
                                                            fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
                                                            textTransform: 'uppercase',
                                                            color: 'text.disabled',
                                                            bgcolor: 'rgba(145, 158, 171, 0.06)',
                                                        }}
                                                    >
                                                        {category}
                                                    </ListSubheader>
                                                )}
                                                <Stack
                                                    component="button"
                                                    type="button"
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1}
                                                    onClick={() => setTreatmentChoice(option)}
                                                    sx={{
                                                        width: '100%',
                                                        px: 2, py: 1,
                                                        border: 0,
                                                        cursor: 'pointer',
                                                        textAlign: 'left',
                                                        bgcolor: isSelected ? 'rgba(0, 167, 111, 0.10)' : 'transparent',
                                                        '&:hover': { bgcolor: isSelected ? 'rgba(0, 167, 111, 0.14)' : 'rgba(145, 158, 171, 0.08)' },
                                                    }}
                                                >
                                                    <Iconify
                                                        icon="eva:checkmark-fill"
                                                        width={16}
                                                        sx={{ flexShrink: 0, color: '#00A76F', opacity: isSelected ? 1 : 0 }}
                                                    />
                                                    <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0, fontWeight: isSelected ? 600 : 400 }} noWrap>
                                                        {option.name}
                                                    </Typography>
                                                    {(option.price || option.price === 0) && (
                                                        <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0 }}>
                                                            £{option.price}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Box>
                                        );
                                    })}
                                </Box>

                                <Box sx={{ p: 2, pt: 1.25, borderTop: '1px solid rgba(145, 158, 171, 0.16)' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                                        {treatmentChoice
                                            ? `Moves ${leadCount} lead${leadCount !== 1 ? 's' : ''} to ${treatmentChoice.name}. Appointments, notes and tasks stay on the lead.`
                                            : 'Only the treatment shown in the table changes — appointments, notes and tasks stay on the lead.'}
                                    </Typography>
                                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                                        <Button
                                            size="small"
                                            color="inherit"
                                            disabled={treatmentLoading}
                                            onClick={() => setTreatmentOpen(false)}
                                            sx={{ ...NO_CAPS, borderRadius: 1.5, color: 'text.secondary' }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained" size="small"
                                            disabled={!treatmentChoice || treatmentLoading}
                                            onClick={handleTreatmentApply}
                                            startIcon={treatmentLoading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
                                            sx={{ ...NO_CAPS, borderRadius: 1.5, px: 2, bgcolor: '#00A76F', '&:hover': { bgcolor: '#007B52' } }}
                                        >
                                            {treatmentLoading ? 'Applying…' : 'Apply'}
                                        </Button>
                                    </Stack>
                                </Box>
                            </Paper>
                        </ClickAwayListener>
                    </Popper>
                </m.div>
            )}
        </AnimatePresence>
    );

    return (
        <>
            {createPortal(bar, document.body)}

            {/* Complete confirm — copy names all three channels the action touches
                (checklist items + due reminders + tasks), since bulk Complete now also
                completes bucket-window reminders (A′) and checklist-less standalone
                tasks (Option 2), not just checklist items. */}
            <ConfirmDialog
                open={doneOpen}
                onClose={() => setDoneOpen(false)}
                title="Complete due items?"
                body={<>Mark the due checklist items, reminders, and tasks as complete for <strong>{selectedRowsLabel}</strong>? This cannot be undone in bulk.</>}
                onConfirm={handleDoneConfirm}
                loading={doneLoading}
                confirmLabel="Complete"
            />

            {/* Column (step) picker menu */}
            <Menu
                anchorEl={columnBtnRef.current}
                open={columnPickerOpen}
                onClose={() => setColumnPickerOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                PaperProps={{ sx: { minWidth: 220, maxWidth: 320, maxHeight: 360, borderRadius: 2 } }}
            >
                {annotatedColumns.map((col) => {
                    const kind = col._actionKind;
                    const verb = kind === 'skip' ? 'Skip' : 'Complete';
                    const iconColor = kind === 'skip' ? 'text.secondary' : '#36b37e';
                    return (
                        <MenuItem
                            key={col.id}
                            onClick={() => handleColumnSelect(col)}
                            sx={{ fontSize: 14, gap: 1 }}
                        >
                            <Iconify
                                icon={kind === 'skip' ? 'eva:slash-outline' : 'eva:checkmark-outline'}
                                width={16}
                                sx={{ color: iconColor, flexShrink: 0 }}
                            />
                            <span>
                                <Typography component="span" sx={{ fontWeight: 500, fontSize: 'inherit' }}>{verb}</Typography>
                                {' — '}{col.title}
                            </span>
                        </MenuItem>
                    );
                })}
            </Menu>

            {/* Skip confirm */}
            <ConfirmDialog
                open={skipOpen}
                onClose={() => setSkipOpen(false)}
                title="Skip all checklist items?"
                body={<>Skip all pending checklist items on <strong>{n('lead')}</strong>? Items will be marked as skipped.</>}
                onConfirm={handleSkipConfirm}
                loading={skipLoading}
                confirmLabel="Skip all"
            />

            {/* Reopen confirm */}
            <ConfirmDialog
                open={reopenOpen}
                onClose={() => setReopenOpen(false)}
                title={`Reopen checklists on ${n('lead')}?`}
                body={<>Completed and skipped items will move back to <strong>In Progress</strong>.</>}
                onConfirm={handleReopenConfirm}
                loading={reopenLoading}
                confirmLabel="Reopen"
            />

            {/* Pause dialog */}
            <Dialog open={pauseOpen} onClose={() => !pauseLoading && setPauseOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Pause until…</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Pause all pending checklist items on <strong>{n('lead')}</strong> until:
                    </Typography>
                    <TextField
                        fullWidth
                        type="date"
                        size="small"
                        value={pauseDate}
                        onChange={(e) => setPauseDate(e.target.value)}
                        inputProps={{ min: new Date().toISOString().split('T')[0] }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => { setPauseOpen(false); setPauseDate(''); }} variant="outlined" disabled={pauseLoading} sx={{ flex: 1, borderRadius: 1.5, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button onClick={handlePauseConfirm} variant="contained" disabled={!pauseDate || pauseLoading} sx={{ flex: 1, borderRadius: 1.5, textTransform: 'none' }}>
                        {pauseLoading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Pause'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Restore confirm (archive page) */}
            <ConfirmDialog
                open={restoreOpen}
                onClose={() => setRestoreOpen(false)}
                title={`Restore ${n('lead')}?`}
                body={<>The selected lead{leadCount !== 1 ? 's' : ''} will be moved back out of the archive.</>}
                onConfirm={handleRestoreConfirm}
                loading={restoreLoading}
                confirmLabel="Restore"
            />

            {/* Change communication (archive page) */}
            {isArchive && (
                <BulkCommunicationPopup
                    open={commOpen}
                    onClose={() => setCommOpen(false)}
                    onCompleted={() => { setCommOpen(false); if (onRefresh) onRefresh(); }}
                    entityLabel='archived lead'
                />
            )}

            {/* Archive confirm */}
            <Dialog open={archiveOpen} onClose={() => !archiveLoading && setArchiveOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Archive {n('lead')}?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select a reason:</Typography>
                    <Select fullWidth size="small" displayEmpty value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} sx={{ borderRadius: 1.5 }}>
                        <MenuItem value="" disabled>Select reason</MenuItem>
                        {ARCHIVE_REASONS.map((r) => (
                            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                        ))}
                    </Select>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setArchiveOpen(false)} variant="outlined" disabled={archiveLoading} sx={{ flex: 1, borderRadius: 1.5, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleArchiveConfirm} variant="contained" color="error" disabled={!archiveReason || archiveLoading} sx={{ flex: 1, borderRadius: 1.5, textTransform: 'none' }}>
                        {archiveLoading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Archive'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

FloatingBulkActionBar.propTypes = {
    practiceId: PropTypes.any.isRequired,
    // Optional lead id -> owning practice id map (All Practices mode). Bulk
    // archive/restore sends one request per practice; absent => primary practice.
    practiceByLeadId: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    onClearSelection: PropTypes.func.isRequired,
    onRefresh: PropTypes.func,
    onBulkComplete: PropTypes.func,
    onOpenBulkReminder: PropTypes.func,
    // P0: called with a skippedGroups object ({ needs_send, needs_data,
    // needs_next_action, needs_artifact } — each an array of lead-ID strings)
    // when bulk Complete skips ≥1 non-bulk-completable items.  Parent renders a
    // persistent (non-auto-dismiss) dialog with group-specific copy explaining
    // what the user must do manually for each affected lead.
    onItemsSkipped: PropTypes.func,
    // When true (Archived Leads table), the destructive "Archive" action is
    // replaced with a "Restore" action that un-archives the selected leads.
    isArchive: PropTypes.bool,
    // Checklist column definitions lifted from NewLeadDataGrid via onChecklistColumnsChange.
    // Each entry: { id, title, item_type, action_config, sort_order, ... }.
    // Drives the "Column" step-picker — only shown when this array is non-empty.
    checklistColumns: PropTypes.arrayOf(PropTypes.object),
    // Active task-due bucket from the new-lead-table tasks view
    // ('due_today' | 'overdue' | 'upcoming' | undefined).  When set, bulk
    // "Mark complete" also completes each selected lead's open standalone
    // reminders whose due date falls in this bucket window.  When undefined
    // (contacts mode or no bucket selected), reminder completion is skipped.
    taskDueBucket: PropTypes.string,
    // Set of row identities ("<leadId>-<leadTreatmentId>" on the flat tables, the
    // lead id elsewhere) that already have at least one recorded treatment payment.
    // Supplied by the table from data it already holds. Used ONLY to decide whether a
    // PAYMENT_METHOD step may be completed YES by a bulk action rather than skipped —
    // see isBulkYesForItem. Absent/empty ⇒ previous behaviour (always skip).
    paidRowIds: PropTypes.instanceOf(Set),
    // { [leadId]: leadTreatmentId } for the rows the host is currently rendering —
    // i.e. WHICH treatment row the Treatment column shows for each lead. Supplying
    // it is what enables the bulk "Treatment" action; omitting it hides the action
    // entirely, so tables that have not opted in are untouched.
    // eslint-disable-next-line react/forbid-prop-types
    treatmentTargets: PropTypes.object,
    // LeadTreatment status the host's table lists (e.g. 'NEW'). Used only to narrow
    // the per-lead lookup for cross-page selections, which are not in treatmentTargets.
    treatmentTargetStatus: PropTypes.string,
    // Opens the treatment picker with the "NHS treatments only" filter already on —
    // true for the NHS board, false everywhere else. Only ever a default: the toggle
    // is always available, and it is ignored when the practice has no NHS treatments.
    nhsOnlyByDefault: PropTypes.bool,
};

FloatingBulkActionBar.defaultProps = {
    onRefresh: null,
    onBulkComplete: null,
    onOpenBulkReminder: null,
    onItemsSkipped: null,
    isArchive: false,
    checklistColumns: [],
    taskDueBucket: undefined,
    paidRowIds: undefined,
    treatmentTargets: null,
    treatmentTargetStatus: undefined,
    nhsOnlyByDefault: false,
};

export default FloatingBulkActionBar;

// Exposed for unit tests — covers the item-level vs lead-level failure-count
// arithmetic. Each helper returns { total, failed, firstError } where `total`
// counts the underlying POSTs issued (item-level for checklist helpers,
// lead-level for tag helper).
export const __testables = {
    bulkChecklistAction,
    bulkCompleteColumn,
    // The yes-vs-skip contract, incl. the PAYMENT_METHOD evidence rule.
    isBulkYesForItem,
    bulkReopenChecklist,
    bulkTagLeads,
    bulkChangeTreatment,
    resolveLeadTreatmentId,
    isNhsTreatment,
    reminderMatchesBucket,
    selectBucketReminders,
    fetchBucketReminders,
    prepareBulkCompleteDiscovery,
    buildBulkCompleteToast,
    // P0 selection-integrity — row-exit union (Wave 1/2a). pruneSelectedLeadIds is
    // re-exported from src/utils/selectionModel (the real home; NewLead imports it
    // from there, not via this test-only surface) so existing bar tests keep passing.
    buildUnionLeadIds,
    countCanonicalLeads,
    pruneSelectedLeadIds,
    // Wave 2d — double-submit guard pin (confirm button disabled while in flight).
    ConfirmDialog,
    // #111 — apiErrMsg array-shape hardening (DRF bare-array rejects).
    apiErrMsg,
    // Option 2 — checklist-less standalone task completion helpers.
    dateStrMatchesBucket,
    taskMatchesBucket,
    selectChecklistlessBucketTasks,
    completeChecklistlessBucketTasks,
};
